import { useState, useRef, useEffect, useCallback } from "react";

// ═══════════════════════════════════════════════════════════════════════════════
// API SERVICE
// ═══════════════════════════════════════════════════════════════════════════════
const API_BASE = import.meta.env.VITE_API_BASE || '/api';

/**
 * Filer ≤ threshold: én XHR (hurtigst for typiske MP4; mindre overhead end chunking).
 * Større filer: parallel chunk-upload (hvis enkelt-POST timeout på hosten).
 * Kan styres med VITE_UPLOAD_CHUNK_AFTER_MB (tal, MB). Standard 100.
 */
const UPLOAD_CHUNK_AFTER_MB = Math.max(
  32,
  Number.parseInt(String(import.meta.env.VITE_UPLOAD_CHUNK_AFTER_MB ?? '100'), 10) || 100
);
const UPLOAD_CHUNK_THRESHOLD = UPLOAD_CHUNK_AFTER_MB * 1024 * 1024;
const UPLOAD_CHUNK_BYTES = 12 * 1024 * 1024;
const UPLOAD_CHUNK_PARALLEL = 3;

function parseJsonError(xhr) {
  try {
    const j = JSON.parse(xhr.responseText || '{}');
    return j.error || xhr.responseText || 'Upload fejlede';
  } catch {
    return xhr.statusText || 'Upload fejlede';
  }
}

function uploadSingleFile(file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.timeout = 0;
    const fd = new FormData();
    fd.append('file', file);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error('Ugyldigt svar fra server'));
        }
      } else {
        reject(new Error(parseJsonError(xhr)));
      }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.open('POST', `${API_BASE}/upload.php`);
    xhr.send(fd);
  });
}

function uploadChunkedFile(file, onProgress) {
  return new Promise((resolve, reject) => {
    const total = Math.ceil(file.size / UPLOAD_CHUNK_BYTES);
    if (total > 200) {
      reject(new Error('Fil for stor til opdelt upload'));
      return;
    }
    const uploadId = crypto.randomUUID().replace(/-/g, '');
    const chunkLoaded = new Array(total).fill(0);

    const report = () => {
      if (!onProgress) return;
      const sum = chunkLoaded.reduce((a, b) => a + b, 0);
      onProgress(Math.min(99, Math.round((sum / file.size) * 100)));
    };

    const postChunk = (index) =>
      new Promise((res, rej) => {
        const start = index * UPLOAD_CHUNK_BYTES;
        const blob = file.slice(start, Math.min(start + UPLOAD_CHUNK_BYTES, file.size));
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', String(index));
        fd.append('total_chunks', String(total));
        fd.append('original_filename', file.name);
        fd.append('file', blob, 'chunk.bin');
        const xhr = new XMLHttpRequest();
        xhr.timeout = 0;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            chunkLoaded[index] = e.loaded;
            report();
          }
        };
        xhr.onload = () => {
          chunkLoaded[index] = blob.size;
          report();
          if (xhr.status >= 200 && xhr.status < 300) {
            res();
          } else {
            rej(new Error(parseJsonError(xhr)));
          }
        };
        xhr.onerror = () => rej(new Error('Netværksfejl (chunk)'));
        xhr.open('POST', `${API_BASE}/upload_chunk.php`);
        xhr.send(fd);
      });

    let next = 0;
    let active = 0;
    let settled = false;

    const finalize = () => {
      const fd = new FormData();
      fd.append('upload_id', uploadId);
      fd.append('total_chunks', String(total));
      fd.append('original_filename', file.name);
      fd.append('total_bytes', String(file.size));
      const xhr = new XMLHttpRequest();
      xhr.timeout = 0;
      xhr.onload = () => {
        if (settled) return;
        if (xhr.status === 200 || xhr.status === 201) {
          if (onProgress) onProgress(100);
          try {
            settled = true;
            resolve(JSON.parse(xhr.responseText));
          } catch {
            settled = true;
            reject(new Error('Ugyldigt svar fra server'));
          }
        } else {
          settled = true;
          reject(new Error(parseJsonError(xhr)));
        }
      };
      xhr.onerror = () => {
        if (!settled) {
          settled = true;
          reject(new Error('Netværksfejl'));
        }
      };
      xhr.open('POST', `${API_BASE}/upload_finalize.php`);
      xhr.send(fd);
    };

    const tryFinalize = () => {
      if (settled) return;
      if (next >= total && active === 0) {
        finalize();
      }
    };

    const pump = () => {
      if (settled) return;
      while (active < UPLOAD_CHUNK_PARALLEL && next < total) {
        const i = next++;
        active++;
        postChunk(i)
          .then(() => {
            active--;
            tryFinalize();
            pump();
          })
          .catch((err) => {
            if (!settled) {
              settled = true;
              reject(err);
            }
          });
      }
    };

    pump();
  });
}

const API = {
  uploadFile(file, onProgress) {
    if (file.size <= UPLOAD_CHUNK_THRESHOLD) {
      return uploadSingleFile(file, onProgress);
    }
    return uploadChunkedFile(file, onProgress);
  },
  async getStatus(id) { const r=await fetch(`${API_BASE}/status.php?project_id=${id}`); if(!r.ok) throw new Error('Status error'); return r.json(); },
  async loadProject(id) { const r=await fetch(`${API_BASE}/project.php?project_id=${id}`); if(!r.ok) throw new Error('Load error'); return r.json(); },
  async loadProjects() {
    const r = await fetch(`${API_BASE}/projects.php`);
    const text = await r.text();
    if (!r.ok) {
      let err = 'Failed to load projects';
      try {
        const j = text ? JSON.parse(text) : {};
        if (j.error) err = j.error;
      } catch { /* ignore */ }
      throw new Error(err + ' (HTTP ' + r.status + ')');
    }
    return text ? JSON.parse(text) : {};
  },
  async loadTimelines(projectId) {
    const r = await fetch(`${API_BASE}/timelines.php?project_id=${projectId}`);
    if (!r.ok) throw new Error('Failed to load timelines');
    return r.json();
  },
  async deleteTimeline(timelineId) {
    const r = await fetch(`${API_BASE}/timelines.php?timeline_id=${timelineId}`, {method: 'DELETE'});
    if (!r.ok) throw new Error('Failed to delete timeline');
    return r.json();
  },
  async deleteProject(projectId) {
    const r = await fetch(`${API_BASE}/projects.php?project_id=${projectId}`, {method: 'DELETE'});
    if (!r.ok) throw new Error('Failed to delete project');
    return r.json();
  },
  async generateChapters(id) {
    const r = await fetch(`${API_BASE}/chapters.php?project_id=${id}`);
    if (!r.ok) throw new Error('Chapters error');
    return r.json();
  },
  async saveTimeline(projectId, name, clips) {
    const r=await fetch(`${API_BASE}/timelines.php`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({project_id:projectId,clips,name})});
    if(!r.ok) throw new Error('Save error'); return r.json();
  },
  async exportTimeline(projectId, clips, format, projectName) {
    const r=await fetch(`${API_BASE}/export.php`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({project_id:projectId,clips,format,project_name:projectName})});
    if(!r.ok) throw new Error('Export error'); return r.blob();
  },
  downloadFile(blob, filename) {
    const url=URL.createObjectURL(blob); const a=document.createElement('a');
    a.href=url; a.download=filename; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// UPLOAD MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function UploadModal({ isOpen, onClose, onUploadComplete }) {
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  const handleFile = async (file) => {
    const allowed = ['video/mp4','video/quicktime','video/x-msvideo','video/x-matroska','video/webm','audio/mpeg','audio/wav','audio/mp4','audio/x-m4a'];
    if (!allowed.includes(file.type)) { setError('Ugyldig filtype. Brug MP4, MOV, AVI, MP3, WAV eller M4A'); return; }
    if (file.size > 500*1024*1024) { setError('Fil for stor (max 500 MB)'); return; }
    setError(null); setUploading(true); setProgress(0);
    
    let modalClosed = false;
    
    try {
      const response = await API.uploadFile(file, (pct) => {
        setProgress(pct);
        // Luk modal når upload når 100% (ikke når response modtages)
        if (pct === 100 && !modalClosed) {
          modalClosed = true;
          // Signal til parent at upload er done (så transcribing overlay vises)
          onUploadComplete({ pending: true, filename: file.name });
          onClose();
        }
      });
      // Nu kommer det rigtige response med project_id
      onUploadComplete(response);
    } catch(err) { 
      setError(err.message); 
    }
    setUploading(false);
  };

  if (!isOpen) return null;
  return (
    <div className="m-overlay" onClick={onClose}>
      <div className="m-box" onClick={e=>e.stopPropagation()}>
        <div className="m-head"><h2>Upload Video / Audio</h2><button onClick={onClose}>✕</button></div>
        <div className={`m-zone${isDragging?' drag':''}${uploading?' busy':''}`}
          onDrop={e=>{e.preventDefault();setIsDragging(false);const f=e.dataTransfer.files[0];if(f)handleFile(f);}}
          onDragOver={e=>{e.preventDefault();setIsDragging(true);}}
          onDragLeave={()=>setIsDragging(false)}
          onClick={()=>!uploading&&ref.current?.click()}>
          {uploading ? (
            <><div className="m-spin"/><div className="m-bar"><div className="m-fill" style={{width:progress+'%'}}/></div><p>Uploader... {progress}%</p></>
          ) : (
            <><div style={{fontSize:'48px'}}>📁</div><p className="m-p1">Træk fil hertil eller klik for at vælge</p><p className="m-p2">MP4 · MOV · AVI · MKV · MP3 · WAV · M4A &nbsp;|&nbsp; Max 500 MB</p></>
          )}
          <input ref={ref} type="file" accept="video/*,audio/*" style={{display:'none'}} onChange={e=>{const f=e.target.files[0];if(f)handleFile(f);}}/>
        </div>
        {error && <div className="m-err"><span>⚠️ {error}</span><button onClick={()=>setError(null)}>✕</button></div>}
      </div>
    </div>
  );
}

const UPLOAD_CSS = `
.m-overlay{position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:9999;display:flex;align-items:center;justify-content:center;}
.m-box{background:var(--bg2);border-radius:12px;width:min(500px,92vw);padding:24px;box-shadow:0 20px 60px rgba(0,0,0,.5);}
.m-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;}
.m-head h2{font-size:18px;color:var(--text);margin:0;}
.m-head button{background:none;border:none;color:var(--text3);font-size:22px;cursor:pointer;line-height:1;}
.m-head button:hover{color:var(--text);}
.m-zone{border:2px dashed var(--border2);border-radius:8px;padding:36px 20px;text-align:center;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:12px;min-height:180px;justify-content:center;transition:all .2s;}
.m-zone:hover,.m-zone.drag{border-color:var(--accent);background:rgba(124,58,237,.06);}
.m-zone.busy{cursor:default;}
.m-p1{font-size:15px;color:var(--text);font-weight:500;margin:0;}
.m-p2{font-size:12px;color:var(--text3);margin:0;}
.m-spin{width:44px;height:44px;border:4px solid var(--surface2);border-top-color:var(--accent);border-radius:50%;animation:spin .8s linear infinite;}
.m-bar{width:100%;height:8px;background:var(--surface2);border-radius:4px;overflow:hidden;}
.m-fill{height:100%;background:var(--accent);transition:width .3s;}
.m-err{display:flex;justify-content:space-between;align-items:center;background:rgba(220,38,38,.1);border:1px solid #DC2626;border-radius:8px;padding:10px 14px;margin-top:14px;color:#FCA5A5;font-size:13px;}
.m-err button{background:none;border:none;color:#FCA5A5;cursor:pointer;font-size:16px;}
.upload-btn{display:flex;align-items:center;gap:6px;background:var(--accent);color:white;border:none;border-radius:8px;padding:6px 12px;font-size:13px;font-weight:600;cursor:pointer;transition:background .15s;}
.upload-btn:hover{background:var(--accent2);}
.upload-btn svg{width:14px;height:14px;}
.status-badge{font-size:11px;padding:3px 9px;border-radius:10px;font-weight:600;margin-left:10px;}
.status-badge.pending{background:rgba(251,191,36,.2);color:#FCD34D;}
.status-badge.processing{background:rgba(14,165,233,.2);color:#38BDF8;animation:pulse 2s ease-in-out infinite;}
.status-badge.ready{background:rgba(5,150,105,.2);color:#34D399;}
.status-badge.error{background:rgba(220,38,38,.2);color:#FCA5A5;}
  .topbar-ai{font-size:11px;padding:3px 9px;border-radius:10px;font-weight:600;background:rgba(124,58,237,.18);color:#c4b5fd;border:1px solid rgba(124,58,237,.4);}
@keyframes pulse{0%,100%{opacity:1;}50%{opacity:.5;}}
.transcribing-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center;z-index:9999;}
.transcribing-box{background:var(--surface);padding:40px 60px;border-radius:12px;text-align:center;box-shadow:0 8px 32px rgba(0,0,0,0.3);}
.transcribing-spinner{width:48px;height:48px;border:4px solid var(--surface2);border-top-color:var(--accent);border-radius:50%;animation:spin 0.8s linear infinite;margin:0 auto 20px;}
@keyframes spin{to{transform:rotate(360deg);}}
.transcribing-text{font-size:18px;font-weight:600;color:var(--text);margin-bottom:8px;}
.transcribing-subtext{font-size:13px;color:var(--text2);}

.project-sidebar{position:fixed;left:0;top:0;bottom:0;width:360px;background:var(--bg);border-right:1px solid var(--surface2);transform:translateX(-100%);transition:transform 0.3s ease;z-index:200;overflow-y:auto;box-shadow:2px 0 12px rgba(0,0,0,0.3);}
.project-sidebar.open{transform:translateX(0);}
.sidebar-header{padding:16px 20px;border-bottom:1px solid var(--surface2);display:flex;align-items:center;justify-content:space-between;background:var(--surface);}
.sidebar-title{font-size:16px;font-weight:600;color:var(--text);text-transform:uppercase;letter-spacing:0.5px;}
.sidebar-close{background:none;border:none;color:var(--text2);cursor:pointer;padding:8px;}
.project-list{padding:12px;}
.project-item{position:relative;background:var(--bg);border:1px solid var(--surface2);border-radius:8px;padding:12px;margin-bottom:12px;cursor:pointer;transition:all 0.2s;}
.project-item:hover{border-color:var(--accent);background:var(--surface);}
.project-item.active{border-color:var(--accent);background:linear-gradient(to right,rgba(124,58,237,0.1),transparent);}
.project-thumb{width:100%;height:80px;background:var(--surface2);border-radius:4px;margin-bottom:8px;object-fit:cover;}
.project-name{font-size:14px;font-weight:500;color:var(--text);margin-bottom:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.project-meta{font-size:12px;color:var(--text2);display:flex;gap:12px;}
.timeline-list{margin-top:8px;padding-left:12px;border-left:2px solid var(--surface2);}
.timeline-list{padding:8px 0 12px 32px;background:var(--bg);border-left:2px solid var(--surface2);margin-left:20px;}
.timeline-item{font-size:12px;color:var(--text2);padding:8px 12px;display:flex;justify-content:space-between;align-items:center;gap:8px;}
.timeline-item.clickable{cursor:pointer;margin:4px 0;border-radius:6px;background:var(--surface);border:1px solid transparent;}
.timeline-item.clickable:hover{color:var(--accent);background:var(--surface2);border-color:var(--accent);transform:translateX(2px);}
.timeline-name{flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.timeline-count{font-weight:600;color:var(--text);}
.project-delete-btn{position:absolute;top:12px;right:12px;background:var(--surface2);color:var(--text2);border:1px solid var(--surface2);border-radius:4px;cursor:pointer;opacity:0;transition:all 0.2s;padding:6px 8px;display:flex;align-items:center;}
.project-delete-btn svg{width:14px;height:14px;}
.project-item:hover .project-delete-btn{opacity:0.7;}
.project-delete-btn:hover{opacity:1 !important;background:var(--surface);border-color:#ef4444;color:#ef4444;transform:scale(1.05);}
.timeline-delete-btn{background:var(--surface2);color:var(--text2);border:1px solid var(--surface2);border-radius:4px;cursor:pointer;opacity:0;transition:all 0.2s;padding:4px 8px;display:flex;align-items:center;margin-left:8px;}
.timeline-delete-btn svg{width:12px;height:12px;}
.timeline-item:hover .timeline-delete-btn{opacity:0.7;}
.timeline-delete-btn:hover{opacity:1 !important;background:var(--surface);border-color:#ef4444;color:#ef4444;}
.sidebar-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:199;display:none;}
.sidebar-overlay.open{display:block;}

.welcome-overlay{position:fixed;inset:0;background:var(--bg);z-index:100;display:flex;align-items:center;justify-content:center;}
.welcome-box{display:flex;flex-direction:column;align-items:center;gap:16px;text-align:center;max-width:420px;padding:48px 32px;}
.welcome-icon{font-size:64px;line-height:1;}
.welcome-box h1{font-size:28px;font-weight:700;color:var(--text);}
.welcome-box p{font-size:15px;color:var(--text2);line-height:1.6;}
.welcome-upload-btn{display:flex;align-items:center;gap:8px;background:var(--accent);color:white;border:none;border-radius:10px;padding:14px 28px;font-size:16px;font-weight:600;cursor:pointer;transition:background .15s;margin-top:8px;}
.welcome-upload-btn:hover{background:var(--accent2);}
.welcome-upload-btn svg{width:18px;height:18px;}
.welcome-hint{font-size:12px;color:var(--text3);}

.hub-view{flex:1;overflow:auto;background:radial-gradient(circle at top, #142445 0%, var(--bg3) 45%, #071026 100%);padding:24px 28px 34px;}
.hub-header{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:24px;}
.hub-title{font-size:28px;font-weight:700;letter-spacing:.2px;}
.hub-subtitle{font-size:13px;color:var(--text2);margin-top:6px;}
.hub-header-actions{display:flex;align-items:center;gap:10px;}
.hub-filter{background:rgba(18,29,52,.8);border:1px solid var(--border2);color:var(--text);border-radius:8px;padding:8px 10px;font-size:12px;}
.hub-upload-btn{display:flex;align-items:center;gap:6px;background:rgba(124,58,237,.18);color:#c4b5fd;border:1px solid rgba(124,58,237,.45);border-radius:8px;padding:8px 10px;font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap;}
.hub-upload-btn:hover{background:rgba(124,58,237,.28);color:#ddd6fe;}
.hub-upload-btn svg{width:13px;height:13px;}
.hub-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:18px;}
.hub-card{background:linear-gradient(180deg, rgba(24,37,66,.92) 0%, rgba(15,26,49,.98) 100%);border:1px solid rgba(255,255,255,.08);border-radius:14px;padding:12px;cursor:pointer;transition:border-color .15s,transform .15s,box-shadow .15s;display:flex;flex-direction:column;}
.hub-card:hover{border-color:rgba(167,139,250,.65);transform:translateY(-1px);box-shadow:0 10px 28px rgba(0,0,0,.28);}
.hub-card.selected{border-color:var(--accent);box-shadow:0 0 0 1px rgba(124,58,237,0.35) inset;}
.hub-card.kbd-focus{box-shadow:0 0 0 2px rgba(124,58,237,0.6) inset;}
.hub-thumb{width:100%;aspect-ratio:16/9;border-radius:10px;object-fit:cover;background:var(--surface2);}
.hub-thumb-empty{display:flex;align-items:center;justify-content:center;color:var(--text3);font-size:26px;}
.hub-card-title{margin-top:11px;font-size:14px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.hub-card-meta{margin-top:6px;display:flex;gap:10px;font-size:12px;color:var(--text2);}
.hub-badges{display:flex;gap:6px;margin-top:8px;min-height:20px;align-items:flex-start;}
.hub-badge{font-size:10px;padding:2px 7px;border-radius:999px;border:1px solid var(--border2);color:var(--text2);}
.hub-badge.latest{color:#c4b5fd;border-color:rgba(124,58,237,.55);background:rgba(124,58,237,.15);}
.hub-badge.last-opened{color:#86efac;border-color:rgba(34,197,94,.45);background:rgba(21,128,61,.2);}
.hub-card-actions{margin-top:auto;padding-top:12px;display:flex;justify-content:space-between;align-items:center;gap:8px;}
.hub-open-btn{background:var(--accent);color:#fff;border:none;border-radius:8px;padding:8px 12px;font-size:12px;font-weight:600;cursor:pointer;}
.hub-open-btn:hover{background:var(--accent2);}
.hub-danger-btn{background:#dc2626;color:#fff;border:1px solid #ef4444;border-radius:8px;padding:7px 10px;cursor:pointer;display:flex;align-items:center;justify-content:center;}
.hub-danger-btn:hover{background:#b91c1c;border-color:#f87171;}
.hub-danger-btn svg{width:13px;height:13px;}
.hub-empty{padding:42px;border:1px dashed var(--border2);border-radius:12px;text-align:center;color:var(--text2);}


.audio-ready{display:flex;flex-direction:column;align-items:center;gap:16px;padding:20px;}
.audio-wave{display:flex;align-items:center;gap:4px;height:60px;}
.audio-bar{width:4px;background:var(--accent);border-radius:2px;transition:height 0.1s;opacity:0.6;}
.audio-bar.playing{animation:audioWave 0.8s ease-in-out infinite alternate;}
@keyframes audioWave{0%{transform:scaleY(0.4);opacity:0.4;}100%{transform:scaleY(1.4);opacity:1;}}
.audio-filename{font-size:12px;color:var(--text3);text-align:center;max-width:300px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}

`;

const MOCK_WORDS = [
  { id:1,  word:"Ja,",         start:6.09,  end:6.35,  index:0  },
  { id:2,  word:"og",          start:6.38,  end:6.50,  index:1  },
  { id:3,  word:"så",          start:6.52,  end:6.65,  index:2  },
  { id:4,  word:"er",          start:6.68,  end:6.80,  index:3  },
  { id:5,  word:"vi",          start:6.82,  end:6.95,  index:4  },
  { id:6,  word:"sådan",       start:6.98,  end:7.24,  index:5  },
  { id:7,  word:"set.",        start:7.26,  end:7.60,  index:6  },
  { id:8,  word:"I",           start:10.11, end:10.22, index:7  },
  { id:9,  word:"optagelse.",  start:10.25, end:11.07, index:8  },
  { id:10, word:"Ja,",         start:12.21, end:12.45, index:9  },
  { id:11, word:"faktisk",     start:12.48, end:12.90, index:10 },
  { id:12, word:"vil",         start:12.93, end:13.10, index:11 },
  { id:13, word:"jeg",         start:13.12, end:13.30, index:12 },
  { id:14, word:"sige",        start:13.33, end:13.65, index:13 },
  { id:15, word:"tusind",      start:13.68, end:14.10, index:14 },
  { id:16, word:"tak,",        start:14.13, end:14.55, index:15 },
  { id:17, word:"fordi",       start:14.58, end:14.95, index:16 },
  { id:18, word:"du",          start:14.97, end:15.10, index:17 },
  { id:19, word:"vil",         start:15.12, end:15.30, index:18 },
  { id:20, word:"gøre",        start:15.33, end:15.70, index:19 },
  { id:21, word:"det",         start:15.73, end:15.88, index:20 },
  { id:22, word:"den",         start:15.90, end:16.05, index:21 },
  { id:23, word:"ulejlighed.", start:16.08, end:16.90, index:22 },
  { id:24, word:"Nå,",         start:17.10, end:17.35, index:23 },
  { id:25, word:"men",         start:17.38, end:17.55, index:24 },
  { id:26, word:"jeg",         start:17.57, end:17.72, index:25 },
  { id:27, word:"synes",       start:17.75, end:18.10, index:26 },
  { id:28, word:"også,",       start:18.13, end:18.55, index:27 },
  { id:29, word:"at",          start:18.58, end:18.70, index:28 },
  { id:30, word:"der",         start:18.73, end:18.90, index:29 },
  { id:31, word:"er",          start:18.92, end:19.05, index:30 },
  { id:32, word:"god",         start:19.08, end:19.40, index:31 },
  { id:33, word:"grund",       start:19.43, end:19.85, index:32 },
  { id:34, word:"til,",        start:19.88, end:20.15, index:33 },
  { id:35, word:"at",          start:20.18, end:20.30, index:34 },
  { id:36, word:"der",         start:20.33, end:20.50, index:35 },
  { id:37, word:"bliver",      start:20.53, end:20.90, index:36 },
  { id:38, word:"åbnet",       start:20.93, end:21.35, index:37 },
  { id:39, word:"op",          start:21.38, end:21.55, index:38 },
  { id:40, word:"for",         start:21.58, end:21.75, index:39 },
  { id:41, word:"nogle",       start:21.78, end:22.10, index:40 },
  { id:42, word:"ting",        start:22.13, end:22.45, index:41 },
  { id:43, word:"omkring",     start:22.48, end:22.90, index:42 },
  { id:44, word:"Tvind.",      start:22.93, end:24.16, index:43 },
  { id:45, word:"Så",          start:25.20, end:25.45, index:44 },
  { id:46, word:"tak",         start:25.48, end:25.75, index:45 },
  { id:47, word:"for",         start:25.78, end:25.95, index:46 },
  { id:48, word:"det.",        start:25.98, end:26.40, index:47 },
  { id:49, word:"Og",          start:26.43, end:26.60, index:48 },
  { id:50, word:"så",          start:26.63, end:26.80, index:49 },
  { id:51, word:"stiller",     start:26.83, end:27.15, index:50 },
  { id:52, word:"jeg",         start:27.18, end:27.35, index:51 },
  { id:53, word:"nogle",       start:27.38, end:27.70, index:52 },
  { id:54, word:"spørgsmål,",  start:27.73, end:28.25, index:53 },
  { id:55, word:"som",         start:28.28, end:28.45, index:54 },
  { id:56, word:"hedder",      start:28.48, end:28.85, index:55 },
  { id:57, word:"sådan",       start:28.88, end:29.20, index:56 },
  { id:58, word:"nogle",       start:29.23, end:29.55, index:57 },
  { id:59, word:"ting,",       start:29.58, end:29.90, index:58 },
  { id:60, word:"og",          start:29.93, end:30.10, index:59 },
  { id:61, word:"så",          start:30.13, end:30.30, index:60 },
  { id:62, word:"ellers",      start:30.33, end:30.70, index:61 },
  { id:63, word:"prøver",      start:30.73, end:31.10, index:62 },
  { id:64, word:"jeg",         start:31.13, end:31.30, index:63 },
  { id:65, word:"at",          start:31.33, end:31.45, index:64 },
  { id:66, word:"fortælle",    start:31.48, end:31.90, index:65 },
  { id:67, word:"så",          start:31.93, end:32.10, index:66 },
  { id:68, word:"meget",       start:32.13, end:32.50, index:67 },
  { id:69, word:"som",         start:32.53, end:32.70, index:68 },
  { id:70, word:"overhovedet", start:32.73, end:33.30, index:69 },
  { id:71, word:"muligt",      start:33.33, end:33.75, index:70 },
  { id:72, word:"uden",        start:33.78, end:34.05, index:71 },
  { id:73, word:"at",          start:34.08, end:34.20, index:72 },
  { id:74, word:"afbryde",     start:34.23, end:34.65, index:73 },
  { id:75, word:"dig.",        start:34.68, end:36.07, index:74 },
  { id:76, word:"kan",         start:37.09, end:37.35, index:75 },
  { id:77, word:"du",          start:37.38, end:37.55, index:76 },
  { id:78, word:"fortælle",    start:37.58, end:37.95, index:77 },
  { id:79, word:"mig,",        start:37.98, end:38.25, index:78 },
  { id:80, word:"hvornår",     start:38.28, end:38.70, index:79 },
  { id:81, word:"du",          start:38.73, end:38.88, index:80 },
  { id:82, word:"og",          start:38.90, end:39.05, index:81 },
  { id:83, word:"din",         start:39.08, end:39.25, index:82 },
  { id:84, word:"familie",     start:39.28, end:39.70, index:83 },
  { id:85, word:"flyttede",    start:39.73, end:40.15, index:84 },
  { id:86, word:"til",         start:40.18, end:40.35, index:85 },
  { id:87, word:"Tvind?",      start:40.38, end:41.00, index:86 },
];

const MOCK_CHAPTERS = [
  { title:"Introduktion",               start:6.09  },
  { title:"Tak og baggrund",            start:10.11 },
  { title:"Familien flytter til Tvind", start:25.20 },
  { title:"Forældrenes forhold",        start:37.09 },
];

const PALETTE = [
  { bg:"#7C3AED", light:"rgba(124,58,237,0.22)",  border:"#6D28D9" },
  { bg:"#0891B2", light:"rgba(8,145,178,0.22)",   border:"#0E7490" },
  { bg:"#059669", light:"rgba(5,150,105,0.22)",   border:"#047857" },
  { bg:"#D97706", light:"rgba(217,119,6,0.22)",   border:"#B45309" },
  { bg:"#DC2626", light:"rgba(220,38,38,0.22)",   border:"#B91C1C" },
  { bg:"#DB2777", light:"rgba(219,39,119,0.22)",  border:"#BE185D" },
  { bg:"#2563EB", light:"rgba(37,99,235,0.22)",   border:"#1D4ED8" },
  { bg:"#65A30D", light:"rgba(101,163,13,0.22)",  border:"#4D7C0F" },
];

function clipColor(clip) { return PALETTE[clip.clip_id % PALETTE.length]; }
function fmtTime(s) { if(s==null)return"—"; const m=Math.floor(s/60),sec=Math.floor(s%60),ms=Math.round((s-Math.floor(s))*10); return `${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}.${ms}`; }
function fmtDur(s)  { return !s?"0s":s<60?`${Math.round(s)}s`:`${Math.floor(s/60)}m${Math.round(s%60)}s`; }

const I = {
  Play:       ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>,
  Pause:      ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>,
  SkipBack:   ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/></svg>,
  Volume:     ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>,
  Plus:       ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>,
  Trash:      ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>,
  GripV:      ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 3H7v2h2V3zm0 4H7v2h2V7zm0 4H7v2h2v-2zm4-8h-2v2h2V3zm0 4h-2v2h2V7zm0 4h-2v2h2v-2zm4-8h-2v2h2V3zm0 4h-2v2h2V7zm0 4h-2v2h2v-2z"/></svg>,
  Search:     ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>,
  Export:     ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>,
  Chevron:    ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>,
  Home:       ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>,
  Folder:     ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/></svg>,
  Bell:       ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>,
  Settings:   ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg>,
  PlayCircle: ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/></svg>,
  List:       ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/></svg>,
  ResizeLeft:  ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M15 18l-6-6 6-6v12z"/></svg>,
  ResizeRight: ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 18l6-6-6-6v12z"/></svg>,
  Upload:      ()=><svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/></svg>,
  X: (props) => <svg width={props?.size || 20} height={props?.size || 20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Save: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>};


const CSS = `
  *{box-sizing:border-box;margin:0;padding:0;}
  :root{
    --bg:#1a1a2e;--bg2:#16213e;--bg3:#0f172a;
    --surface:#1e2a3a;--surface2:#243447;
    --border:rgba(255,255,255,0.08);--border2:rgba(255,255,255,0.14);
    --text:#e2e8f0;--text2:#94a3b8;--text3:#64748b;
    --accent:#7C3AED;--accent2:#6D28D9;--accent-glow:rgba(124,58,237,0.3);
    --topbar-h:48px;--sidebar-w:52px;--transcript-w:340px;--sequence-w:300px;--bottom-h:40px;
    --mono:'JetBrains Mono','Fira Code',monospace;
  }
  body{background:var(--bg3);color:var(--text);font-family:-apple-system,'SF Pro Display',sans-serif;overflow:hidden;}
  .app-root{display:flex;flex-direction:column;height:100vh;}

  .topbar{height:var(--topbar-h);background:var(--bg2);border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;padding:0 16px;gap:16px;flex-shrink:0;}
  .topbar-left{display:flex;align-items:center;gap:10px;}
  .topbar-back{background:none;border:none;color:var(--text2);cursor:pointer;font-size:16px;padding:4px 8px;border-radius:4px;}
  .topbar-back:hover{background:var(--surface);}
  .topbar-back svg{width:16px;height:16px;display:block;}
  .topbar-breadcrumb{font-size:13px;}.bc-project{color:var(--text2);}.bc-sep{color:var(--text3);margin:0 4px;}.bc-file{color:var(--text);font-weight:500;}
  .topbar-center{font-size:12px;color:var(--text3);}
  .topbar-right{display:flex;align-items:center;gap:10px;}
  .share-btn{display:flex;align-items:center;gap:6px;background:var(--accent);color:white;border:none;border-radius:8px;padding:6px 14px;font-size:13px;font-weight:600;cursor:pointer;}
  .share-btn:hover{background:var(--accent2);}
  .share-btn svg{width:14px;height:14px;}
  .search-input-wrap{display:flex;align-items:center;gap:6px;background:var(--surface);border:1px solid var(--border2);border-radius:8px;padding:5px 10px;}
  .search-input-wrap svg{width:14px;height:14px;color:var(--text3);}
  .search-input-wrap input{background:none;border:none;color:var(--text);font-size:13px;outline:none;width:120px;}

  .app-body{display:flex;flex:1;overflow:hidden;}
  .sidebar{width:var(--sidebar-w);background:var(--bg2);border-right:1px solid var(--border);display:flex;flex-direction:column;justify-content:space-between;padding:8px 0;flex-shrink:0;}
  .sidebar-top,.sidebar-bottom{display:flex;flex-direction:column;align-items:center;gap:4px;}
  .sidebar-btn{width:36px;height:36px;display:flex;align-items:center;justify-content:center;background:none;border:none;color:var(--text3);border-radius:8px;cursor:pointer;}
  .sidebar-btn svg{width:18px;height:18px;}
  .sidebar-btn:hover,.sidebar-btn.active{background:var(--surface2);color:var(--text);}

  /* ── Transcript ── */
  .transcript-panel{width:var(--transcript-w);background:var(--bg2);border-right:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden;flex-shrink:0;user-select:none;}
  .transcript-panel.resizing{cursor:col-resize;}
  .transcript-panel.resizing .word-span{cursor:col-resize!important;}
  .panel-header{display:flex;align-items:center;justify-content:space-between;padding:10px 14px;border-bottom:1px solid var(--border);flex-shrink:0;}
  .panel-title{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;}
  .lang-badge{background:var(--surface2);color:var(--text2);font-size:10px;font-weight:700;padding:2px 6px;border-radius:4px;letter-spacing:0.5px;}
  .panel-actions{display:flex;gap:4px;}
  .icon-btn{width:28px;height:28px;display:flex;align-items:center;justify-content:center;background:none;border:none;color:var(--text3);border-radius:6px;cursor:pointer;font-size:14px;}
  .icon-btn svg{width:15px;height:15px;}
  .icon-btn:hover{background:var(--surface2);color:var(--text);}
  .search-bar{display:flex;align-items:center;gap:8px;padding:6px 14px;border-bottom:1px solid var(--border);flex-shrink:0;}
  .search-bar svg{width:13px;height:13px;color:var(--text3);flex-shrink:0;}
  .search-bar input{background:none;border:none;color:var(--text);font-size:12px;outline:none;width:100%;}
  .search-bar input::placeholder{color:var(--text3);}
  .chapters-section{flex-shrink:0;border-bottom:1px solid var(--border);}
  .chapters-toggle{display:flex;align-items:center;gap:6px;width:100%;padding:8px 14px;background:none;border:none;color:var(--text2);font-size:12px;cursor:pointer;}
  .chapters-toggle:hover{background:var(--surface);}
  .chapters-toggle svg{width:14px;height:14px;}
  .ai-badge{background:rgba(124,58,237,0.2);color:#a78bfa;font-size:10px;padding:1px 6px;border-radius:10px;margin-left:2px;}
  .chevron{margin-left:auto;transition:transform 0.2s;display:flex;}.chevron svg{width:14px;height:14px;}.chevron.open{transform:rotate(90deg);}
  .chapters-list{padding:2px 0 6px;}
  .chapter-item{display:flex;align-items:center;gap:8px;width:100%;padding:5px 14px 5px 20px;background:none;border:none;color:var(--text2);font-size:12px;cursor:pointer;text-align:left;}
  .chapter-item:hover{background:var(--surface2);color:var(--text);}
  .ch-arrow svg{width:12px;height:12px;}.ch-time{color:var(--accent);font-size:11px;font-family:var(--mono);width:52px;}
  .tip-bar{display:flex;align-items:center;gap:6px;padding:5px 14px;font-size:11px;color:#a78bfa;background:rgba(124,58,237,0.08);border-bottom:1px solid var(--border);flex-shrink:0;min-height:28px;}
  .tip-bar strong{font-weight:700;}
  .tip-dot{width:5px;height:5px;background:var(--accent);border-radius:50%;flex-shrink:0;}
  .transcript-body{flex:1;overflow-y:auto;padding:12px 14px;}
  .transcript-body::-webkit-scrollbar{width:4px;}
  .transcript-body::-webkit-scrollbar-thumb{background:var(--surface2);border-radius:2px;}
  .sentence-block{margin-bottom:7px;padding:0;}
  .sentence-time{font-family:var(--mono);font-size:10px;color:var(--text3);margin-bottom:4px;cursor:pointer;}
  .sentence-time:hover{color:var(--accent);}
.chapter-heading{display:flex;align-items:center;gap:10px;padding:6px 10px;margin:16px -14px 4px;background:linear-gradient(90deg,rgba(124,58,237,0.18) 0%,transparent 100%);border-left:3px solid var(--accent);cursor:pointer;transition:background .15s;}
.chapter-heading:hover{background:linear-gradient(90deg,rgba(124,58,237,0.28) 0%,transparent 100%);}
.chapter-heading-icon{font-size:11px;font-weight:700;color:var(--accent);letter-spacing:.05em;text-transform:uppercase;flex-shrink:0;}
.chapter-heading-title{font-size:13px;font-weight:600;color:var(--text);flex:1;line-height:1.3;}
.chapter-heading-time{font-family:var(--mono);font-size:10px;color:var(--accent);opacity:.7;}

  .sentence-text{font-size:15px;line-height:1.52;color:var(--text);font-weight:430;letter-spacing:.01em;word-spacing:.03em;text-wrap:pretty;}
  .word-span{cursor:pointer;border-radius:4px;padding:2px 0;transition:background-color 0.08s, box-shadow 0.08s;}
  .word-span:hover{background-color:rgba(255,255,255,0.07);}

  /* ── Resize håndtag ── */
  .resize-handle{
    display:inline-flex; align-items:center; justify-content:center;
    width:16px; height:18px;
    border:1px solid currentColor;
    border-radius:3px;
    cursor:col-resize;
    vertical-align:middle;
    margin:0 1px;
    background:var(--bg2);
    transition:transform 0.1s, box-shadow 0.1s;
    position:relative;
    z-index:2;
  }
  .resize-handle svg{width:12px;height:12px;}
  .resize-handle:hover{transform:scaleX(1.15);box-shadow:0 0 6px currentColor;}
  .resize-handle-start{border-radius:4px 2px 2px 4px;}
  .resize-handle-end{border-radius:2px 4px 4px 2px;}

  .add-to-timeline-btn{display:flex;align-items:center;gap:6px;background:var(--accent);color:white;border:none;border-radius:20px;padding:7px 14px;font-size:13px;font-weight:600;cursor:pointer;box-shadow:0 4px 20px var(--accent-glow);animation:popIn 0.15s ease-out;}
  .add-to-timeline-btn:hover{background:var(--accent2);}
  .add-to-timeline-btn svg{width:14px;height:14px;}
  @keyframes popIn{from{transform:scale(0.8);opacity:0;}to{transform:scale(1);opacity:1;}}

  /* ── Video ── */
  .video-panel{flex:1;background:var(--bg3);display:flex;flex-direction:column;overflow:hidden;min-width:0;}
  .video-area{flex:1;display:flex;align-items:center;justify-content:center;padding:24px;}
  .video-placeholder{width:100%;max-width:520px;aspect-ratio:16/9;background:var(--surface);border-radius:12px;display:flex;align-items:center;justify-content:center;border:1px solid var(--border);}
  .avatar-silhouette{display:flex;flex-direction:column;align-items:center;opacity:0.3;}
  .avatar-head{width:56px;height:56px;background:var(--text3);border-radius:50%;}
  .avatar-body{width:80px;height:70px;background:var(--text3);border-radius:50% 50% 0 0;margin-top:4px;}
  .video-controls{display:flex;align-items:center;gap:12px;padding:10px 20px;flex-shrink:0;}
  .ctrl-btn{width:32px;height:32px;display:flex;align-items:center;justify-content:center;background:none;border:none;color:var(--text3);border-radius:8px;cursor:pointer;}
  .ctrl-btn svg{width:18px;height:18px;}
  .ctrl-btn:hover{color:var(--text);background:var(--surface);}
  .play-btn{width:42px;height:42px;display:flex;align-items:center;justify-content:center;background:var(--accent);border:none;border-radius:50%;color:white;cursor:pointer;box-shadow:0 2px 12px var(--accent-glow);}
  .play-btn svg{width:20px;height:20px;}
  .play-btn:hover{background:var(--accent2);transform:scale(1.05);}
  .time-display{font-family:var(--mono);font-size:12px;color:var(--text2);margin-left:8px;}
  .timeline-bar{height:6px;background:var(--surface2);position:relative;cursor:pointer;flex-shrink:0;}
  .timeline-bar:hover{height:8px;}
  .timeline-progress{height:100%;background:var(--accent);pointer-events:none;}
  .timeline-thumb{position:absolute;top:50%;transform:translate(-50%,-50%);width:12px;height:12px;background:white;border-radius:50%;box-shadow:0 0 0 2px var(--accent);pointer-events:none;transition:left 0.05s linear;}

  /* ── Sequence ── */
  .sequence-panel{width:var(--sequence-w);background:var(--bg2);border-left:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden;flex-shrink:0;}
  .seq-tabs{display:flex;border-bottom:1px solid var(--border);flex-shrink:0;}
  .seq-tab{display:flex;align-items:center;gap:6px;flex:1;padding:11px 14px;background:none;border:none;border-bottom:2px solid transparent;color:var(--text3);font-size:13px;cursor:pointer;}
  .seq-tab svg{width:14px;height:14px;}
  .seq-tab:hover{color:var(--text);}.seq-tab.active{color:var(--text);border-bottom-color:var(--accent);}
  .seq-count{background:var(--accent);color:white;font-size:10px;font-weight:700;padding:1px 6px;border-radius:10px;}
  .seq-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;padding:24px;text-align:center;}
  .seq-empty-icon svg{width:32px;height:32px;color:var(--text3);}
  .seq-empty-title{font-size:14px;font-weight:600;color:var(--text2);}
  .seq-empty-hint{font-size:12px;color:var(--text3);line-height:1.6;}
  .seq-header{display:flex;align-items:center;justify-content:space-between;padding:10px 14px 6px;flex-shrink:0;}
  .seq-total{font-size:13px;color:var(--text2);}
  .seq-play-all{width:26px;height:26px;display:flex;align-items:center;justify-content:center;background:var(--surface2);border:none;border-radius:50%;color:var(--text);cursor:pointer;}
  .seq-play-all svg{width:13px;height:13px;}
  .seq-play-all:hover{background:var(--accent);}
  .seq-miniline{display:flex;height:22px;margin:0 14px 6px;border-radius:4px;overflow:hidden;gap:1px;flex-shrink:0;}
  .seq-miniclip{height:100%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:white;cursor:pointer;min-width:16px;border-radius:2px;transition:filter 0.1s;}
  .seq-miniclip:hover{filter:brightness(1.2);}
  .seq-miniclip.sel{filter:brightness(1.3);}
  .cursor-hint{display:flex;align-items:center;gap:6px;padding:3px 14px 4px;font-size:10px;color:var(--text3);flex-shrink:0;}
  .cursor-dot{width:6px;height:6px;border:2px solid var(--text3);border-radius:50%;flex-shrink:0;}
  .seq-clips{flex:1;overflow-y:auto;padding:0 8px 4px;}
  .seq-clips::-webkit-scrollbar{width:4px;}
  .seq-clips::-webkit-scrollbar-thumb{background:var(--surface2);border-radius:2px;}
  .seq-clips.dragging-mode{background:rgba(124,58,237,0.03);border-radius:8px;}

  /* Drop zones */
  .drop-zone{height:10px;display:flex;align-items:center;padding:0 8px;cursor:pointer;position:relative;transition:height 0.18s ease;}
  .drop-zone:hover{height:14px;}
  .drop-zone.active{height:44px;background:rgba(124,58,237,0.12);border:2px dashed rgba(124,58,237,0.5);border-radius:8px;margin:2px 0;}
  .drop-zone-inner{width:100%;display:flex;align-items:center;justify-content:center;}
  .drop-zone:not(.active):not(.cursor) .drop-zone-inner::after{content:'';display:block;width:100%;height:1px;background:var(--border);}
  .drop-zone:hover:not(.active) .drop-zone-inner::after{background:var(--border2);height:2px;}
  .drop-zone-cursor-dot{width:100%;height:2px;background:var(--accent);border-radius:1px;box-shadow:0 0 6px var(--accent-glow);}
  .drop-zone-label{display:flex;align-items:center;gap:5px;color:#a78bfa;font-size:11px;font-weight:600;animation:fadeIn 0.1s ease;}
  .drop-zone-label svg{width:12px;height:12px;}
  @keyframes fadeIn{from{opacity:0;}to{opacity:1;}}

  /* Clip cards */
  .clip-card{display:flex;align-items:center;gap:6px;background:var(--surface);border:1px solid var(--border);border-radius:8px;padding:8px 8px 8px 6px;cursor:pointer;transition:all 0.15s;}
  .clip-card:hover:not(.ghost){border-color:var(--border2);background:var(--surface2);}
  .clip-card.active{border-color:var(--accent);box-shadow:0 0 0 1px var(--accent);}
  .clip-card.selected{background:var(--surface2);}
  .clip-card.ghost{opacity:0.35;background:transparent;border-style:dashed;cursor:default;}
  .clip-card.being-dragged{display:none;}
  .clip-drag-handle{width:16px;flex-shrink:0;opacity:0.4;cursor:grab;display:flex;align-items:center;}
  .clip-drag-handle svg{width:14px;height:14px;}
  .clip-drag-handle:hover{opacity:0.8;}
  .clip-number{width:20px;height:20px;border-radius:4px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:white;flex-shrink:0;}
  .clip-body{flex:1;min-width:0;}
  .clip-times{display:flex;align-items:center;gap:4px;font-family:var(--mono);font-size:10px;color:var(--text3);margin-bottom:3px;}
  .clip-arrow{color:var(--text3);}
  .clip-dur{color:var(--accent);font-size:9px;}
  .clip-label{font-size:12px;color:var(--text2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .clip-actions{display:flex;flex-direction:column;gap:2px;flex-shrink:0;}
  .clip-play-btn,.clip-del-btn{width:24px;height:24px;display:flex;align-items:center;justify-content:center;background:none;border:none;border-radius:4px;cursor:pointer;}
  .clip-play-btn svg{width:16px;height:16px;color:var(--text3);}
  .clip-del-btn svg{width:14px;height:14px;color:var(--text3);}
  .clip-play-btn:hover{background:rgba(124,58,237,0.2);}
  .clip-play-btn:hover svg{color:var(--accent);}
  .clip-del-btn:hover{background:rgba(220,38,38,0.2);}
  .clip-del-btn:hover svg{color:#ef4444;}
  .export-section{padding:10px 12px 12px;border-top:1px solid var(--border);flex-shrink:0;}
  .btn-clear{background:var(--surface2);color:var(--text2);border:1px solid var(--surface2);padding:10px 16px;border-radius:6px;cursor:pointer;display:flex;align-items:center;gap:6px;font-weight:500;margin-right:8px;}
.btn-clear:hover{background:var(--surface);border-color:#ef4444;color:#ef4444;}
.export-btn-main{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;background:var(--accent);color:white;border:none;border-radius:10px;padding:10px;font-size:13px;font-weight:600;cursor:pointer;margin-bottom:8px;}
  .export-btn-main svg{width:15px;height:15px;}
  .export-btn-main:hover{background:var(--accent2);}
  .export-formats{display:flex;align-items:center;justify-content:center;gap:4px;flex-wrap:wrap;}
  .fmt-btn{background:none;border:none;color:var(--text3);font-size:10px;cursor:pointer;padding:2px 4px;border-radius:3px;}
  .fmt-btn:hover{color:var(--accent);}
  .fmt-dot{color:var(--text3);font-size:10px;}

  /* ── Bottom timeline ── */
  .bottom-timeline{height:var(--bottom-h);background:var(--bg2);border-top:1px solid var(--border);position:relative;cursor:pointer;flex-shrink:0;overflow:hidden;transition:background 0.3s;}
  .bottom-timeline.seq{background:#0d1b2a;}
  .btl-mode-badge{position:absolute;top:50%;transform:translateY(-50%);left:8px;font-size:9px;font-weight:700;letter-spacing:0.5px;padding:2px 7px;border-radius:10px;z-index:2;pointer-events:none;}
  .btl-mode-badge.raw{background:rgba(255,255,255,0.07);color:var(--text3);}
  .btl-mode-badge.seq{background:rgba(124,58,237,0.3);color:#a78bfa;}
  .btl-marker{position:absolute;top:0;height:100%;display:flex;align-items:center;transition:filter 0.15s;}
  .btl-marker:hover{filter:brightness(1.3);}
  .btl-marker.seq-clip{border-right:1px solid rgba(255,255,255,0.08);}
  .btl-marker.btl-sel{filter:brightness(1.4);}
  .btl-num{font-size:9px;font-weight:700;padding:0 4px;pointer-events:none;}
  .btl-insert-cursor{position:absolute;top:0;bottom:0;width:3px;background:rgba(255,255,255,0.9);pointer-events:none;z-index:4;box-shadow:0 0 8px rgba(255,255,255,0.5);transition:left 0.1s;}
  .btl-playhead{position:absolute;top:0;bottom:0;width:2px;background:white;pointer-events:none;z-index:3;transition:left 0.05s linear;}
  .btl-playhead::before{content:'';position:absolute;top:0;left:-4px;width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:8px solid white;}
`;

// ═══════════════════════════════════════════════════════════════════════════════
// TranscriptView
// ═══════════════════════════════════════════════════════════════════════════════
function TranscriptView({ words, clips, currentTime, selectedClipId,
                          onAddClip, onSeek, onTranscriptInteract, onResizeClip, chapters = [] }) {
  const [selStart,  setSelStart]  = useState(null);
  const [selEnd,    setSelEnd]    = useState(null);
  const [dragging,  setDragging]  = useState(false);
  const [addPos,    setAddPos]    = useState(null);
  // Resize state
  // resizeEdge: "start" | "end" | null
  const [resizeEdge, setResizeEdge] = useState(null);
  const [resizingClipId, setResizingClipId] = useState(null);
  const activeSentenceRef = useRef(null);
  const bodyRef           = useRef(null);
  const chapterRefs       = useRef({});

  // word_index → clip
  const wordToClip = {};
  clips.forEach(clip => {
    words.forEach(w => {
      if (w.start >= clip.start_sec && w.end <= clip.end_sec + 0.05)
        wordToClip[w.index] = clip;
    });
  });

  // Find det valgte klip
  const selectedClip = clips.find(c => c.clip_id === selectedClipId) || null;

  // Ord-indeks for første og sidste ord i det valgte klip
  let selClipFirstIdx = null, selClipLastIdx = null;
  if (selectedClip) {
    const clipWords = words.filter(w => w.start >= selectedClip.start_sec && w.end <= selectedClip.end_sec + 0.05);
    if (clipWords.length) {
      selClipFirstIdx = clipWords[0].index;
      selClipLastIdx  = clipWords[clipWords.length - 1].index;
    }
  }

  const playingIdx = words.findIndex(w => currentTime >= w.start && currentTime <= w.end);

  // Auto-scroll til aktiv sætning - brug currentTime som dep, ikke playingIdx
  useEffect(() => {
    const el = activeSentenceRef.current;
    const body = bodyRef.current;
    if (!el || !body) return;
    const bodyRect = body.getBoundingClientRect();
    const elRect   = el.getBoundingClientRect();
    const relTop   = elRect.top - bodyRect.top;
    const bodyH    = body.clientHeight;
    if (relTop < 60 || relTop > bodyH - 120) {
      body.scrollTo({ top: body.scrollTop + relTop - 60, behavior: 'smooth' });
    }
  }, [currentTime]);

  // ── Normal tekst-selektion ──────────────────────────────────────────────────
  const onMD = useCallback((w, e) => {
    if (resizeEdge) return; // resize håndteres separat
    e.preventDefault();
    setSelStart(w.index); setSelEnd(w.index);
    setDragging(true); setAddPos(null);
    onTranscriptInteract();
  }, [resizeEdge, onTranscriptInteract]);

  const onME = useCallback((w) => {
    if (dragging) setSelEnd(w.index);
    // Under resize: opdater klipgrænsen dynamisk
    if (resizeEdge && resizingClipId !== null) {
      onResizeClip(resizingClipId, resizeEdge, w);
    }
  }, [dragging, resizeEdge, resizingClipId, onResizeClip]);

  const onMU = useCallback((e) => {
    if (resizeEdge) {
      setResizeEdge(null); setResizingClipId(null); return;
    }
    if (!dragging) return;
    setDragging(false);
    if (selStart !== null && selEnd !== null && selStart !== selEnd)
      setAddPos({ x: e.clientX, y: e.clientY });
  }, [dragging, resizeEdge, selStart, selEnd]);

  useEffect(() => {
    const up = () => {
      if (dragging) setDragging(false);
      if (resizeEdge) { setResizeEdge(null); setResizingClipId(null); }
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, [dragging, resizeEdge]);

  // ── Resize håndtag mousedown ────────────────────────────────────────────────
  const onResizeHandleDown = useCallback((e, clipId, edge) => {
    e.preventDefault(); e.stopPropagation();
    setResizeEdge(edge);
    setResizingClipId(clipId);
  }, []);

  const selRange = selStart !== null && selEnd !== null
    ? [Math.min(selStart, selEnd), Math.max(selStart, selEnd)] : null;

  const handleAdd = () => {
    if (!selRange) return;
    const sel = words.filter(w => w.index >= selRange[0] && w.index <= selRange[1]);
    if (!sel.length) return;
    onAddClip({ start_sec: sel[0].start, end_sec: sel[sel.length-1].end, label: sel.map(w=>w.word).join(" ").slice(0,70) });
    setSelStart(null); setSelEnd(null); setAddPos(null);
  };

  // Grupper i mere naturlige sætninger (undgå små "single-word" linjer).
  const sentences = [];
  let cur = [];
  let curChars = 0;
  words.forEach((w, i) => {
    cur.push(w);
    curChars += String(w.word || '').length + 1;
    const n = words[i + 1];
    if (!n) {
      sentences.push([...cur]);
      cur = [];
      curChars = 0;
      return;
    }
    const pause = n.start - w.end;
    const punctuationBreak = /[.?!,:;]$/.test(String(w.word || ''));
    const nextStartsUpper = /^[A-ZÆØÅ]/.test(String(n.word || ''));
    const longPauseBreak = pause > 1.3;
    const softPauseBreak = pause > 0.95 && (cur.length >= 10 || curChars >= 68);
    const longSentenceBreak = cur.length >= 22 || curChars >= 165;
    const capitalStartBreak = nextStartsUpper && cur.length >= 4;
    if (punctuationBreak || longPauseBreak || softPauseBreak || longSentenceBreak || capitalStartBreak) {
      sentences.push([...cur]);
      cur = [];
      curChars = 0;
    }
  });
  if (cur.length) sentences.push(cur);

  // Merge meget korte sætninger med næste for mere regulært tekstflow.
  const mergedSentences = [];
  for (let i = 0; i < sentences.length; i++) {
    const s = sentences[i];
    const isTiny = s.length <= 3;
    if (isTiny && mergedSentences.length > 0) {
      mergedSentences[mergedSentences.length - 1] = [
        ...mergedSentences[mergedSentences.length - 1],
        ...s
      ];
    } else {
      mergedSentences.push([...s]);
    }
  }

  const isResizing = resizeEdge !== null;

  return (
    <div className={`transcript-panel${isResizing?" resizing":""}`} onMouseUp={onMU}>
      {/* Tekst */}
      <div className="transcript-body" ref={bodyRef}>
        {(() => {
          // Tildel hvert kapitel til præcis ÉN sætning: den nærmeste
          const chapterForSentence = {};
          chapters.forEach(ch => {
            let bestIdx = 0;
            let bestDist = Infinity;
            sentences.forEach((s, si) => {
              const dist = Math.abs(s[0].start - ch.start);
              if (dist < bestDist) { bestDist = dist; bestIdx = si; }
            });
            chapterForSentence[bestIdx] = ch;
          });

          return mergedSentences.map((s, si) => {
          const isActive = s.some(w => w.index === playingIdx);
          const chapterHere = chapterForSentence[si] || null;
          
          return (
            <div key={si} ref={isActive ? activeSentenceRef : null}>
              {chapterHere && (
                <div
                  className="chapter-heading"
                  ref={el => { if (el && chapterHere) chapterRefs.current[chapterHere.start] = el; }}
                  onClick={() => {
                    onSeek(chapterHere.start);
                    onTranscriptInteract();
                    // Scroll heading til toppen af transcript-body
                    const el   = chapterRefs.current[chapterHere.start];
                    const body = bodyRef.current;
                    if (el && body) {
                      const bodyRect = body.getBoundingClientRect();
                      const elRect   = el.getBoundingClientRect();
                      body.scrollTo({ top: body.scrollTop + (elRect.top - bodyRect.top), behavior: 'smooth' });
                    }
                  }}>
                  <span className="chapter-heading-icon">▶</span>
                  <span className="chapter-heading-title">{chapterHere.title}</span>
                  <span className="chapter-heading-time">{fmtTime(chapterHere.start)}</span>
                </div>
              )}
            <div className="sentence-block">
              <div className="sentence-text">
                {s.map(w => {
                  const clip        = wordToClip[w.index] || null;
                  const isSelected  = selRange ? w.index >= selRange[0] && w.index <= selRange[1] : false;
                  const isPlaying   = w.index === playingIdx;
                  const isInSelClip = selectedClip && clip && clip.clip_id === selectedClip.clip_id;
                  const isFirstOfSelClip = w.index === selClipFirstIdx;
                  const isLastOfSelClip  = w.index === selClipLastIdx;
                  const color = clip ? clipColor(clip) : null;

                  const style = {};
                  if (color)      { style.backgroundColor=color.light; style.borderBottom=`2px solid ${color.border}`; style.borderRadius="2px"; }
                  if (isSelected) { style.backgroundColor="rgba(139,92,246,0.38)"; style.borderBottom="2px solid #7C3AED"; style.borderRadius="2px"; }
                  if (isPlaying)  { style.backgroundColor="rgba(251,191,36,0.32)"; style.borderBottom="2px solid #F59E0B"; style.borderRadius="2px"; }
                  // Forstærk highlight på det valgte klip
                  if (isInSelClip) { style.backgroundColor=color.light.replace("0.22","0.38"); }

                  return (
                    <span key={w.id} style={{position:"relative", display:"inline"}}>
                      {/* Resize-håndtag START — vises kun på første ord i valgt klip */}
                      {isFirstOfSelClip && (
                        <span
                          className="resize-handle resize-handle-start"
                          style={{borderColor: clipColor(selectedClip).bg, color: clipColor(selectedClip).bg}}
                          onMouseDown={e => onResizeHandleDown(e, selectedClip.clip_id, "start")}
                          title="Træk for at ændre startpunkt"
                        >
                          <I.ResizeLeft/>
                        </span>
                      )}

                      <span
                        className="word-span"
                        style={style}
                        data-start={w.start} data-end={w.end} data-index={w.index}
                        onMouseDown={e => onMD(w, e)}
                        onMouseEnter={() => onME(w)}
                        onMouseUp={onMU}
                      >
                        {w.word}{" "}
                      </span>

                      {/* Resize-håndtag SLUT — vises kun på sidste ord i valgt klip */}
                      {isLastOfSelClip && (
                        <span
                          className="resize-handle resize-handle-end"
                          style={{borderColor: clipColor(selectedClip).bg, color: clipColor(selectedClip).bg}}
                          onMouseDown={e => onResizeHandleDown(e, selectedClip.clip_id, "end")}
                          title="Træk for at ændre slutpunkt"
                        >
                          <I.ResizeRight/>
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
            </div>
          );
        })})()}
      </div>

      {/* + Tilføj popup */}
      {addPos && (
        <button className="add-to-timeline-btn"
          style={{position:"fixed",left:addPos.x-44,top:addPos.y-52,zIndex:1000}}
          onClick={handleAdd}>
          <I.Plus/> Tilføj
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// VideoPreview
// ═══════════════════════════════════════════════════════════════════════════════
function VideoPreview({ currentTime, duration, isPlaying, onPlayPause, onSeek, audioUrl, isVideo, onTimeUpdate, onDurationChange }) {
  const barRef        = useRef(null);
  const audioRef      = useRef(null);
  const prevPlaying   = useRef(false);
  const prevTime      = useRef(0);
  const pct           = duration ? (currentTime / duration) * 100 : 0;

  // Sync play/pause
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying && !prevPlaying.current) {
      console.log('Attempting to play video/audio...');
      a.play().catch(err => {
        console.error('Play failed:', err.message);
        // Hvis autoplay blokeres, trigger fra user action
        if (err.name === 'NotAllowedError') {
          console.warn('Autoplay blocked - play requires user interaction');
          onPlayPause(); // Toggle tilbage til paused
        }
      });
    } else if (!isPlaying && prevPlaying.current) {
      console.log('Pausing...');
      a.pause();
    }
    prevPlaying.current = isPlaying;
  });

  // Sync seek (kun ved store spring)
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const diff = Math.abs(prevTime.current - currentTime);
    if (diff > 1.0) {
      a.currentTime = currentTime;
    }
    prevTime.current = currentTime;
  });

  return (
    <div className="video-panel">
      <div className="video-area">

        {audioUrl && (
          isVideo ? (
            <video
              ref={audioRef}
              src={audioUrl}
              onTimeUpdate={e => onTimeUpdate && onTimeUpdate(e.target.currentTime)}
              onDurationChange={e => onDurationChange && onDurationChange(e.target.duration)}
              onEnded={() => onPlayPause && onPlayPause()}
              onClick={onPlayPause}
              style={{width:'100%', height:'100%', objectFit:'contain', background:'#000', cursor:'pointer'}}
              controls={false}
            />
          ) : (
            <audio
              ref={audioRef}
              src={audioUrl}
              onTimeUpdate={e => onTimeUpdate && onTimeUpdate(e.target.currentTime)}
              onDurationChange={e => onDurationChange && onDurationChange(e.target.duration)}
              onEnded={() => onPlayPause && onPlayPause()}
              style={{display:'none'}}
            />
          )
        )}

        <div className="video-placeholder" style={{display: (audioUrl && isVideo) ? 'none' : 'flex'}}>
          {audioUrl ? (
            <div className="audio-ready">
              <div className="audio-wave">
                {[...Array(12)].map((_,i) => (
                  <div key={i} className={`audio-bar${isPlaying?' playing':''}`}
                    style={{animationDelay:`${i*0.08}s`, height:`${20+Math.sin(i*0.8)*16}px`}}/>
                ))}
              </div>
              <div className="audio-filename">{audioUrl.split('/').pop().replace(/^[^_]+_/, '')}</div>
            </div>
          ) : (
            <div className="avatar-silhouette">
              <div className="avatar-head"/><div className="avatar-body"/>
            </div>
          )}
        </div>
      </div>

      <div className="video-controls">
        <button className="ctrl-btn" onClick={() => onSeek(Math.max(0, currentTime-10))}><I.SkipBack/></button>
        <button className="play-btn" onClick={onPlayPause}>{isPlaying ? <I.Pause/> : <I.Play/>}</button>
        <button className="ctrl-btn"><I.Volume/></button>
        <span className="time-display">{fmtTime(currentTime)}{duration ? ' / ' + fmtTime(duration) : ''}</span>
      </div>

      <div className="timeline-bar" ref={barRef}
        onClick={e => {
          const r = barRef.current.getBoundingClientRect();
          onSeek((e.clientX - r.left) / r.width * (duration || 0));
        }}>
        <div className="timeline-progress" style={{width:`${pct}%`}}/>
        <div className="timeline-thumb"    style={{left:`${pct}%`}}/>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DropZone
// ═══════════════════════════════════════════════════════════════════════════════
function DropZone({ slotIndex, isActive, isInsertCursor, onDragOver, onDrop, onClick }) {
  return (
    <div className={`drop-zone${isActive?" active":""}${isInsertCursor?" cursor":""}`}
      onDragOver={e=>{ e.preventDefault(); onDragOver(slotIndex); }}
      onDrop={e=>{ e.preventDefault(); onDrop(slotIndex); }}
      onClick={()=>onClick(slotIndex)}>
      <div className="drop-zone-inner">
        {isActive && <div className="drop-zone-label"><I.Plus/>slip her</div>}
        {!isActive && isInsertCursor && <div className="drop-zone-cursor-dot"/>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ClipCard
// ═══════════════════════════════════════════════════════════════════════════════
function ClipCard({ clip, listIndex, isActive, isSelected, isDragging, isGhost,
                    onSelect, onPlay, onDelete, onDragStart, onDragEnd }) {
  const color = clipColor(clip);
  if (isGhost) {
    return (
      <div className="clip-card ghost" style={{borderColor:color.bg}}>
        <div className="clip-number" style={{backgroundColor:color.bg}}>{listIndex+1}</div>
        <div className="clip-body">
          <div className="clip-times"><span>{fmtTime(clip.start_sec)}</span><span className="clip-arrow">→</span><span>{fmtTime(clip.end_sec)}</span></div>
          <div className="clip-label">{clip.label}</div>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`clip-card${isActive?" active":""}${isSelected?" selected":""}${isDragging?" being-dragged":""}`}
      style={isSelected ? {borderColor:color.bg, boxShadow:`0 0 0 1px ${color.bg}, 0 0 12px ${color.light}`} : {}}
      draggable
      onDragStart={e=>{ e.dataTransfer.effectAllowed="move"; onDragStart(listIndex); }}
      onDragEnd={onDragEnd}
      onClick={()=>onSelect(clip)}     // ← klik parkerer, starter IKKE afspilning
    >
      <div className="clip-drag-handle" style={{color:color.bg}}><I.GripV/></div>
      <div className="clip-number" style={{backgroundColor:color.bg}}>{listIndex+1}</div>
      <div className="clip-body">
        <div className="clip-times">
          <span>{fmtTime(clip.start_sec)}</span><span className="clip-arrow">→</span>
          <span>{fmtTime(clip.end_sec)}</span>
          <span className="clip-dur">({fmtDur(clip.end_sec-clip.start_sec)})</span>
        </div>
        <div className="clip-label">{clip.label}</div>
      </div>
      <div className="clip-actions">
        {/* Play-knap starter afspilning */}
        <button className="clip-play-btn" title="Afspil fra dette klip"
          onClick={e=>{ e.stopPropagation(); onPlay(clip); }}>
          <I.PlayCircle/>
        </button>
        <button className="clip-del-btn" title="Slet klip"
          onClick={e=>{ e.stopPropagation(); onDelete(clip.clip_id); }}>
          <I.Trash/>
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SequencePanel
// ═══════════════════════════════════════════════════════════════════════════════
function SequencePanel({ clips, currentTime, insertCursor, selectedClipId,
                         onInsertCursorChange, onSelectClip,
                         onPlayFromClip, onDeleteClip, onReorderClips, onSave, onClearTimeline, onExport }) {
  const [dragFrom, setDragFrom] = useState(null);
  const [dropSlot, setDropSlot] = useState(null);
  const [tab,      setTab]      = useState("sequence");

  const isDragging = dragFrom !== null;
  const totalDur   = clips.reduce((s,c)=>s+(c.end_sec-c.start_sec),0);
  const activeClipI= clips.findIndex(c=>currentTime>=c.start_sec&&currentTime<=c.end_sec);

  const handleDragStart = i => { setDragFrom(i); setDropSlot(null); };
  const handleDragEnd   = () => { setDragFrom(null); setDropSlot(null); };
  const handleDragOver  = si => setDropSlot(si);

  const handleDrop = si => {
    if (dragFrom===null) return;
    let target = si; if (si > dragFrom) target = si - 1;
    if (dragFrom===target) { setDragFrom(null); setDropSlot(null); return; }
    const arr=[...clips]; const [m]=arr.splice(dragFrom,1); arr.splice(target,0,m);
    let nc=insertCursor;
    if (insertCursor===dragFrom) nc=target;
    else if (dragFrom<insertCursor&&si>dragFrom) nc=Math.max(0,insertCursor-1);
    else if (dragFrom>insertCursor&&si<=insertCursor) nc=Math.min(clips.length,insertCursor+1);
    onReorderClips(arr); onInsertCursorChange(nc);
    setDragFrom(null); setDropSlot(null);
  };

  return (
    <div className="sequence-panel">
      <div className="seq-tabs">
        <button className={`seq-tab${tab==="sequence"?" active":""}`} onClick={()=>setTab("sequence")}>
          <I.List/>Sekvens{clips.length>0&&<span className="seq-count">{clips.length}</span>}
        </button>
        <button className={`seq-tab${tab==="comments"?" active":""}`} onClick={()=>setTab("comments")}>Kommentarer</button>
      </div>

      {tab==="sequence" ? (
        clips.length===0 ? (
          <div className="seq-empty">
            <div className="seq-empty-icon"><I.List/></div>
            <div className="seq-empty-title">Ingen klip i sekvensen</div>
            <div className="seq-empty-hint">Marker tekst i transskriptionen og klik + for at tilføje klip</div>
          </div>
        ) : (<>
          <div className="seq-header">
            <span className="seq-total">Total: {fmtDur(totalDur)}</span>
            <button className="seq-play-all" onClick={()=>onPlayFromClip(clips[0])}><I.Play/></button>
          </div>
          <div className="seq-miniline">
            {clips.map((c,i)=>{ const col=clipColor(c); const w=totalDur>0?((c.end_sec-c.start_sec)/totalDur)*100:100/clips.length;
              return <div key={c.clip_id} className={`seq-miniclip${c.clip_id===selectedClipId?" sel":""}`}
                style={{width:`${w}%`,backgroundColor:col.bg,outline:c.clip_id===selectedClipId?`2px solid white`:undefined}}
                onClick={()=>onSelectClip(c)} title={c.label}>{i+1}</div>; })}
          </div>
          <div className="cursor-hint">
            {isDragging ? <><span style={{color:"#a78bfa"}}>●</span>Slip klippet i et mellemrum</> : <><span className="cursor-dot"/>Klik for at vælge · play-knap for at afspille</>}
          </div>
          <div className={`seq-clips${isDragging?" dragging-mode":""}`}>
            <DropZone slotIndex={0} isActive={isDragging&&dropSlot===0&&dragFrom!==0}
              isInsertCursor={!isDragging&&insertCursor===0}
              onDragOver={handleDragOver} onDrop={handleDrop} onClick={onInsertCursorChange}/>
            {clips.map((clip,i)=>(
              <div key={clip.clip_id}>
                {isDragging && dragFrom===i
                  ? <ClipCard clip={clip} listIndex={i} isGhost onPlay={()=>{}} onDelete={()=>{}} onSelect={()=>{}} onDragStart={()=>{}} onDragEnd={()=>{}}/>
                  : <ClipCard clip={clip} listIndex={i}
                      isActive={i===activeClipI}
                      isSelected={clip.clip_id===selectedClipId}
                      isDragging={false}
                      onSelect={onSelectClip}     // klik = parkér, ingen afspilning
                      onPlay={onPlayFromClip}     // play-knap = afspil
                      onDelete={onDeleteClip}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}/>
                }
                <DropZone slotIndex={i+1}
                  isActive={isDragging&&dropSlot===i+1&&dragFrom!==i&&dragFrom!==i+1}
                  isInsertCursor={!isDragging&&insertCursor===i+1}
                  onDragOver={handleDragOver} onDrop={handleDrop} onClick={onInsertCursorChange}/>
              </div>
            ))}
          </div>
          <div className="export-section">
            <button className="btn-clear" onClick={() => onClearTimeline && onClearTimeline()}>
              🗑️ Ryd
            </button>
            <button className="export-btn-main" onClick={() => onSave && onSave()}>
              💾 Gem Timeline
            </button>
            <div className="export-formats">
              <button className="fmt-btn" onClick={() => onExport && onExport('fcpxml')}>FCP XML</button>
              <span className="fmt-dot">·</span>
              <button className="fmt-btn" onClick={() => onExport && onExport('premiere_xml')}>Premiere</button>
              <span className="fmt-dot">·</span>
              <button className="fmt-btn" onClick={() => onExport && onExport('edl')}>EDL</button>
              <span className="fmt-dot">·</span>
              <button className="fmt-btn" onClick={() => onExport && onExport('srt')}>SRT</button>
            </div>
          </div>
        </>)
      ) : (
        <div className="seq-empty"><div className="seq-empty-hint">Ingen kommentarer endnu</div></div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BottomTimeline
// ═══════════════════════════════════════════════════════════════════════════════
function BottomTimeline({ mode, rawDuration, rawCurrentTime, clips, seqTime, onRawSeek, onSeqSeek, insertCursor, selectedClipId }) {
  const ref = useRef(null);
  const totalSeqDur = clips.reduce((s,c)=>s+(c.end_sec-c.start_sec),0);
  const handleClick = e => {
    const r=ref.current.getBoundingClientRect(), pct=(e.clientX-r.left)/r.width;
    if (mode==="raw") onRawSeek(pct*rawDuration); else onSeqSeek(pct*totalSeqDur);
  };

  if (mode==="raw") {
    const pct=rawDuration?(rawCurrentTime/rawDuration)*100:0;
    return (
      <div className="bottom-timeline raw" ref={ref} onClick={handleClick}>
        <div className="btl-mode-badge raw">Råoptagelse</div>
        {clips.map((c,i)=>{ const col=clipColor(c); const lp=rawDuration?(c.start_sec/rawDuration)*100:0; const wp=rawDuration?((c.end_sec-c.start_sec)/rawDuration)*100:0;
          return <div key={c.clip_id} className={`btl-marker${c.clip_id===selectedClipId?" btl-sel":""}`}
            style={{left:`${lp}%`,width:`${wp}%`,backgroundColor:col.light,borderTop:`3px solid ${col.bg}`,
              boxShadow:c.clip_id===selectedClipId?`0 0 0 2px white inset`:undefined}}>
            <span className="btl-num" style={{color:col.bg}}>{i+1}</span></div>; })}
        <div className="btl-playhead" style={{left:`${pct}%`}}/>
      </div>
    );
  }

  let offset=0;
  const positions=clips.map(c=>{ const dur=c.end_sec-c.start_sec; const l=totalSeqDur?(offset/totalSeqDur)*100:0; const w=totalSeqDur?(dur/totalSeqDur)*100:100/clips.length; offset+=dur; return{c,l,w}; });
  const seqPct=totalSeqDur?(seqTime/totalSeqDur)*100:0;
  const insertLeft=insertCursor<positions.length?positions[insertCursor]?.l??100:100;

  return (
    <div className="bottom-timeline seq" ref={ref} onClick={handleClick}>
      <div className="btl-mode-badge seq">Sekvens ▶</div>
      {positions.map(({c,l,w},i)=>{ const col=clipColor(c); return <div key={c.clip_id} className={`btl-marker seq-clip${c.clip_id===selectedClipId?" btl-sel":""}`}
        style={{left:`${l}%`,width:`${w}%`,backgroundColor:col.light,borderTop:`3px solid ${col.bg}`,
          boxShadow:c.clip_id===selectedClipId?`0 0 0 2px white inset`:undefined}}>
        <span className="btl-num" style={{color:col.bg}}>{i+1}</span></div>; })}
      <div className="btl-insert-cursor" style={{left:`${insertLeft}%`}}/>
      <div className="btl-playhead" style={{left:`${seqPct}%`}}/>
    </div>
  );
}

// ── Sidebar + TopBar ──────────────────────────────────────────────────────────
function Sidebar({ onHomeClick, onFolderClick }) {
  return (
    <div className="sidebar">
      <div className="sidebar-top"><button className="sidebar-btn active" onClick={onHomeClick}><I.Home/></button><button className="sidebar-btn"><I.Search/></button><button className="sidebar-btn" onClick={onFolderClick}><I.Folder/></button></div>
      <div className="sidebar-bottom"><button className="sidebar-btn"><I.Bell/></button><button className="sidebar-btn"><I.Settings/></button></div>
    </div>
  );
}
function TopBar({ onUploadClick, onProjectsClick, status, filename, showUpload = true, showHome = false, onHomeClick, aiUsageText = '' }) {
  return (
    <div className="topbar">
      <div className="topbar-left">
        {showHome && (
          <button className="topbar-back" onClick={onHomeClick} title="Tilbage til forside">
            <I.Home/>
          </button>
        )}
        <h1 className="topbar-filename">{filename ? filename.replace(/\.(mp4|mp3|wav|m4a)$/i, "") : "Ingen fil valgt"}</h1>
        {status && (
          <span className={`status-badge ${status}`}>
            {status === 'pending'    && '⏳ Starter...'}
            {status === 'processing' && '🔄 Transkriberer...'}
            {status === 'ready'      && '✅ Klar'}
            {status === 'error'      && '❌ Fejl'}
          </span>
        )}
        {aiUsageText && <span className="topbar-ai">{aiUsageText}</span>}
      </div>
      <div className="topbar-right">
        {showUpload && (
          <button className="upload-btn" onClick={onUploadClick}>
            <I.Upload/> Upload
          </button>
        )}
      </div>
    </div>
  );
}

function HomeConfirmModal({ isOpen, onSaveAndLeave, onLeaveWithoutSave, onCancel }) {
  if (!isOpen) return null;
  return (
    <div className="m-overlay" onClick={onCancel}>
      <div className="m-box" onClick={(e) => e.stopPropagation()}>
        <div className="m-head">
          <h2>Tilbage til forside</h2>
          <button onClick={onCancel}>✕</button>
        </div>
        <p className="m-p2" style={{ marginBottom: 16 }}>
          Du har clips i timeline. Vil du gemme før du går tilbage til forsiden?
        </p>
        <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
          <button className="welcome-upload-btn" style={{ marginTop: 0, justifyContent: 'center' }} onClick={onSaveAndLeave}>
            Gem timeline og gå til forside
          </button>
          <button className="hub-danger-btn" style={{ width: '100%', justifyContent: 'center', borderRadius: 10, padding: '12px 14px' }} onClick={onLeaveWithoutSave}>
            Forlad uden at gemme
          </button>
          <button className="hub-open-btn" style={{ width: '100%', justifyContent: 'center', borderRadius: 10, padding: '12px 14px' }} onClick={onCancel}>
            Annuller
          </button>
        </div>
      </div>
    </div>
  );
}

function TimelineNameModal({ isOpen, value, onChange, onConfirm, onCancel }) {
  if (!isOpen) return null;
  return (
    <div className="m-overlay" onClick={onCancel}>
      <div className="m-box" onClick={(e) => e.stopPropagation()}>
        <div className="m-head">
          <h2>Gem timeline</h2>
          <button onClick={onCancel}>✕</button>
        </div>
        <div className="m-p1" style={{ textAlign: 'left', width: '100%', marginBottom: 8 }}>Navn til timeline:</div>
        <input
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onConfirm();
            if (e.key === 'Escape') onCancel();
          }}
          style={{
            width: '100%',
            padding: '12px 14px',
            borderRadius: 10,
            border: '1px solid var(--border2)',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: 16,
            outline: 'none'
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button className="btn-clear" style={{ marginRight: 0 }} onClick={onCancel}>Annuller</button>
          <button className="hub-open-btn" style={{ padding: '10px 18px' }} onClick={onConfirm}>Gem</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT APP
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// AUTO-GENERER KAPITLER FRA ORD
// ═══════════════════════════════════════════════════════════════════════════════
function generateChapters(words) {
  if (!words || words.length === 0) return [];
  
  const totalDur = words[words.length - 1]?.end || 0;
  // Interval: hvert 2. minut, minimum 4 kapitler, maksimum 12
  const targetCount = Math.min(12, Math.max(4, Math.ceil(totalDur / 120)));
  const interval    = totalDur / targetCount;
  
  const chapters = [];
  for (let i = 0; i < targetCount; i++) {
    const targetTime = i * interval;
    // Find nærmeste ord til dette tidspunkt
    const word = words.reduce((best, w) => 
      Math.abs(w.start - targetTime) < Math.abs(best.start - targetTime) ? w : best
    , words[0]);
    
    // Brug de næste 4-6 ord som kapitel-titel
    const idx   = word.index ?? words.indexOf(word);
    const slice = words.slice(idx, idx + 6).map(w => w.word).join(' ');
    const title = slice.length > 40 ? slice.substring(0, 38) + '…' : slice;
    
    chapters.push({ title, start: word.start });
  }
  return chapters;
}

// ProjectSidebar komponent - indsæt før App komponenten
function ProjectSidebar({
  isOpen, 
  onClose, 
  projects, 
  currentProjectId, 
  onSelectProject,
  onNewProject,
  onLoadTimeline,
  onDeleteTimeline,
  onDeleteProject,
  onTimelineSaved
}) {
  const [localProjects, setLocalProjects] = useState(projects || []);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [timelines, setTimelines] = useState({});
  
  // Poll for window projects if prop is empty
  useEffect(() => {
    if (!projects || projects.length === 0) {
      const interval = setInterval(() => {
        if (window.__GLING_PROJECTS__ && window.__GLING_PROJECTS__.length > 0) {
          console.log('📥 ProjectSidebar got data from window:', window.__GLING_PROJECTS__.length);
          setLocalProjects(window.__GLING_PROJECTS__);
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    } else {
      setLocalProjects(projects);
    }
  }, [projects]);

  // Reload timelines når der kommer save event
  useEffect(() => {
    if (onTimelineSaved && onTimelineSaved.projectId) {
      const pid = onTimelineSaved.projectId;
      if (expandedProjects[pid]) {
        // Reload timelines for expanded project
        fetch(`${API_BASE}/timelines.php?project_id=${pid}`)
          .then(r => r.json())
          .then(data => {
            setTimelines(prev => ({ ...prev, [pid]: data.timelines || [] }));
            console.log('🔄 Timelines reloaded in sidebar for project:', pid);
          })
          .catch(err => console.error('Timeline reload failed:', err));
      }
    }
  }, [onTimelineSaved, expandedProjects]);

  console.log('🎨 ProjectSidebar rendering, projects count:', projects?.length);








  useEffect(() => {
    if (isOpen) {
      // Load all projects
      // Projects kommer via props
    }
  }, [isOpen]);

  const toggleProject = async (projectId) => {
    const isExpanded = expandedProjects[projectId];
    
    if (!isExpanded && !timelines[projectId]) {
      // Load timelines for this project
      try {
        const data = await API.loadTimelines(projectId);
        setTimelines(prev => ({ ...prev, [projectId]: data.timelines || [] }));
      } catch (err) {
        console.error('Failed to load timelines:', err);
      }
    }
    
    setExpandedProjects(prev => ({ ...prev, [projectId]: !isExpanded }));
  };

  const formatDuration = (sec) => {
    if (!sec) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const formatUsd = (v) => {
    const n = Number(v || 0);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (n < 0.01) return '<$0.01';
    return `$${n.toFixed(2)}`;
  };

  return (
    <>
      <div className={`sidebar-overlay${isOpen ? ' open' : ''}`} onClick={onClose} />
      <div className={`project-sidebar${isOpen ? ' open' : ''}`}>
        <div className="sidebar-header">
          <h2 className="sidebar-title">Projekter</h2>
          <button className="sidebar-close" onClick={onClose}>
            <I.X size={20} />
          </button>
        </div>

        <div className="project-list">
          {(localProjects || []).map(p => (
            <div key={p.project_id}>
              <div 
                className={`project-item${currentProjectId === p.project_id ? ' active' : ''}`}
                onClick={() => {
                  if (currentProjectId !== p.project_id) {
                    onSelectProject(p.project_id);
                  }
                  toggleProject(p.project_id);
                }}
              >
                {p.thumbnail_url && (
                  <img 
                    src={p.thumbnail_url} 
                    alt={p.filename} 
                    className="project-thumb"
                  />
                )}
                <div className="project-name" title={p.filename}>
                  {p.is_video ? '🎬' : '🎵'} {p.filename}
                </div>
                <div className="project-meta">
                  <span>{formatDuration(p.duration_sec)}</span>
                  <span>{p.word_count || 0} ord</span>
                  {p.timeline_count > 0 && <span>{p.timeline_count} timeline{p.timeline_count > 1 ? 's' : ''}</span>}
                </div>
                <button 
                  className="project-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteProject && onDeleteProject(p.project_id);
                  }}
                  title="Slet hele projektet"
                >
                  <I.Trash/>
                </button>
              </div>

              {expandedProjects[p.project_id] && timelines[p.project_id] && (
                <div className="timeline-list">
                  {(timelines[p.project_id] || []).map(t => {
                    const clipCount = Array.isArray(t.clips) 
                      ? t.clips.length 
                      : (typeof t.clips === 'string' ? JSON.parse(t.clips).length : 0);
                    
                    return (
                      <div 
                        key={t.timeline_id} 
                        className="timeline-item clickable"
                      >
                        <span 
                          className="timeline-name"
                          onClick={(e) => {
                            e.stopPropagation();
                            onLoadTimeline && onLoadTimeline(t);
                          }}
                        >
                          📝 {t.name}
                        </span>
                        <span className="timeline-count">{clipCount} clips</span>
                        <button 
                          className="timeline-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteTimeline && onDeleteTimeline(t.timeline_id, p.project_id);
                          }}
                          title="Slet timeline"
                        >
                          <I.Trash/>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          <button 
            className="project-item" 
            style={{border: '2px dashed var(--accent)', background: 'transparent'}}
            onClick={onNewProject}
          >
            <div style={{textAlign: 'center', color: 'var(--accent)'}}>
              <I.Plus size={32} style={{margin: '12px auto'}} />
              <div>Upload nyt projekt</div>
            </div>
          </button>
        </div>
      </div>
    </>
  );
}

function ProjectHub({
  projects,
  selectedProjectId,
  lastOpenedProjectId,
  onSelectProject,
  onOpenProject,
  onDeleteProject,
  onUploadClick
}) {
  const [hubFilter, setHubFilter] = useState('all');

  const formatDuration = (sec) => {
    if (!sec) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const formatUsd = (v) => {
    const n = Number(v || 0);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (n < 0.01) return '<$0.01';
    return `$${n.toFixed(2)}`;
  };

  const visibleProjects = (projects || []).filter((p) => {
    if (hubFilter === 'with-timelines') {
      return (p.timeline_count || 0) > 0;
    }
    if (hubFilter === 'last-opened') {
      return lastOpenedProjectId ? p.project_id === lastOpenedProjectId : true;
    }
    return true;
  });

  useEffect(() => {
    if (!visibleProjects.length) return;
    const hasSelected = visibleProjects.some((p) => p.project_id === selectedProjectId);
    if (!hasSelected) {
      onSelectProject(visibleProjects[0].project_id);
    }
  }, [visibleProjects, selectedProjectId, onSelectProject]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!visibleProjects.length) return;
      const currentIndex = Math.max(0, visibleProjects.findIndex((p) => p.project_id === selectedProjectId));

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = Math.min(visibleProjects.length - 1, currentIndex + 1);
        onSelectProject(visibleProjects[nextIndex].project_id);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prevIndex = Math.max(0, currentIndex - 1);
        onSelectProject(visibleProjects[prevIndex].project_id);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const selectedProject = visibleProjects[currentIndex];
        if (!selectedProject) return;
        onOpenProject(selectedProject.project_id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visibleProjects, selectedProjectId, onSelectProject, onOpenProject]);

  return (
    <div className="hub-view">
      <div className="hub-header">
        <div>
          <div className="hub-title">Projektoversigt</div>
          <div className="hub-subtitle">Vælg et projekt og gå direkte til editoren.</div>
        </div>
        <div className="hub-header-actions">
          <select className="hub-filter" value={hubFilter} onChange={(e) => setHubFilter(e.target.value)}>
            <option value="all">Vis: Alle projekter</option>
            <option value="with-timelines">Vis: Kun med timelines</option>
            <option value="last-opened">Vis: Senest åbnede</option>
          </select>
          <button className="hub-upload-btn" onClick={onUploadClick}>
            <I.Upload/> Upload nyt projekt
          </button>
        </div>
      </div>

      {(!visibleProjects || visibleProjects.length === 0) ? (
        <div className="hub-empty">
          Ingen projekter endnu. Upload en video eller lydfil for at komme i gang.
        </div>
      ) : (
        <div className="hub-grid">
          {visibleProjects.map((p, idx) => {
            const isSelected = selectedProjectId === p.project_id;
            return (
              <div
                key={p.project_id}
                className={`hub-card${isSelected ? ' selected kbd-focus' : ''}`}
                onClick={() => onOpenProject(p.project_id)}
                onMouseEnter={() => onSelectProject(p.project_id)}
              >
                {p.thumbnail_url ? (
                  <img src={p.thumbnail_url} alt={p.filename} className="hub-thumb" />
                ) : (
                  <div className="hub-thumb hub-thumb-empty">{p.is_video ? '🎬' : '🎵'}</div>
                )}
                <div className="hub-card-title" title={p.filename}>{p.filename}</div>
                <div className="hub-card-meta">
                  <span>{formatDuration(p.duration_sec)}</span>
                  <span>{p.word_count || 0} ord</span>
                  <span>{p.timeline_count || 0} timelines</span>
                  {formatUsd(p?.ai_usage?.total_usd) && <span>AI {formatUsd(p.ai_usage.total_usd)}</span>}
                </div>
                <div className="hub-badges">
                  {idx === 0 && <span className="hub-badge latest">Nyeste</span>}
                  {lastOpenedProjectId === p.project_id && <span className="hub-badge last-opened">Senest åbnet</span>}
                </div>
                <div className="hub-card-actions">
                  <button
                    className="hub-open-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenProject(p.project_id);
                    }}
                  >
                    Åbn projekt
                  </button>
                  <button
                    className="hub-danger-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteProject(p.project_id);
                    }}
                    title="Slet projekt"
                  >
                    <I.X/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TimelineChoiceModal({
  isOpen,
  projectName,
  timelines,
  onNewTimeline,
  onChooseTimeline,
  onCancel
}) {
  if (!isOpen) return null;

  const getClipCount = (timeline) => {
    if (Array.isArray(timeline.clips)) return timeline.clips.length;
    if (typeof timeline.clips === 'string') {
      try {
        const parsed = JSON.parse(timeline.clips);
        return Array.isArray(parsed) ? parsed.length : 0;
      } catch {
        return 0;
      }
    }
    return 0;
  };

  return (
    <div className="m-overlay" onClick={onCancel}>
      <div className="m-box" onClick={(e) => e.stopPropagation()}>
        <div className="m-head">
          <h2>Vælg timeline</h2>
          <button onClick={onCancel}>✕</button>
        </div>
        <p className="m-p2" style={{ marginBottom: 14 }}>
          Projekt: <strong>{projectName || 'Ukendt projekt'}</strong>
        </p>
        <button className="welcome-upload-btn" style={{ width: '100%', justifyContent: 'center', marginTop: 0 }} onClick={onNewTimeline}>
          Start ny timeline
        </button>
        <div style={{ marginTop: 12, maxHeight: 260, overflowY: 'auto', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          {(timelines || []).map((t) => (
            <button
              key={t.timeline_id}
              className="timeline-item clickable"
              style={{ width: '100%', margin: '4px 0', justifyContent: 'space-between' }}
              onClick={() => onChooseTimeline(t)}
            >
              <span className="timeline-name">📝 {t.name}</span>
              <span className="timeline-count">{getClipCount(t)} clips</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


export default function App() {
  const [viewMode, setViewMode] = useState('hub');
  const [clips,         setClips]         = useState([]);
  const [clipIdSeq,     setClipIdSeq]     = useState(0);
  const [rawTime,       setRawTime]       = useState(12.21);
  const [seqTime,       setSeqTime]       = useState(0);
  const [isPlaying,     setIsPlaying]     = useState(false);
  const [tlMode,        setTlMode]        = useState("raw");
  const [insertCursor,  setInsertCursor]  = useState(0);
  const [selectedClipId,setSelectedClipId]= useState(null);

  const [projectId,      setProjectId]      = useState(null);
  const [projectStatus,  setProjectStatus]  = useState(null);
  const [projectAiUsage, setProjectAiUsage] = useState({ total_usd: 0, operations: 0 });
  const [transcriptionError, setTranscriptionError] = useState(null);
  const [projectFilename,setProjectFilename]= useState('Demo');
  const [showUploadModal,setShowUploadModal]= useState(false);
  const [words,          setWords]          = useState([]);
  const [audioUrl,       setAudioUrl]       = useState(null);
  const [isVideo,        setIsVideo]        = useState(false);
  const [rawDur,         setRawDur]         = useState(0);
  const [chapters,       setChapters]       = useState([]);

  const [showProjectSidebar, setShowProjectSidebar] = useState(false);
  const [allProjects,        setAllProjects]        = useState(window.__GLING_PROJECTS__ || []);
  const [timelineSavedTrigger, setTimelineSavedTrigger] = useState(null);
  const [projectTimelines,   setProjectTimelines]   = useState([]);
  const [hubSelectedProjectId, setHubSelectedProjectId] = useState(null);
  const [hubLastOpenedProjectId, setHubLastOpenedProjectId] = useState(null);
  const [showTimelineChoiceModal, setShowTimelineChoiceModal] = useState(false);
  const [timelineChoiceProjectId, setTimelineChoiceProjectId] = useState(null);
  const [timelineChoiceProjectName, setTimelineChoiceProjectName] = useState('');
  const [timelineChoiceTimelines, setTimelineChoiceTimelines] = useState([]);
  const [showHomeConfirmModal, setShowHomeConfirmModal] = useState(false);
  const [showTimelineNameModal, setShowTimelineNameModal] = useState(false);
  const [timelineNameInput, setTimelineNameInput] = useState('');
  const timer = useRef(null);
  const timelineNameResolver = useRef(null);

  // Listen for projects updates from preload (main.jsx)
  useEffect(() => {
    window.__GLING_UPDATE__ = (projects) => {
      setAllProjects(projects);
    };
    return () => { window.__GLING_UPDATE__ = null; };
  }, []);

  useEffect(() => {
    API.loadProjects()
      .then(d => setAllProjects(d.projects || []))
      .catch(e => console.error('Failed to load projects', e));
  }, []);

  
  useEffect(() => {
    if (!isPlaying) { clearInterval(timer.current); return; }
    timer.current = setInterval(() => {
      if (tlMode==="raw") {
        setRawTime(t => { if(t>=rawDur){setIsPlaying(false);return 0;} return +(t+0.1).toFixed(2); });
      } else {
        const tot=clips.reduce((s,c)=>s+(c.end_sec-c.start_sec),0);
        setSeqTime(t => { if(t>=tot){setIsPlaying(false);return 0;} return +(t+0.1).toFixed(2); });
      }
    }, 100);
    return () => clearInterval(timer.current);
  }, [isPlaying, tlMode, clips]);

  const seqToRaw = () => {
    if (tlMode!=="sequence"||!clips.length) return rawTime;
    let rem=seqTime;
    for (const c of clips) { const dur=c.end_sec-c.start_sec; if(rem<=dur) return c.start_sec+rem; rem-=dur; }
    return clips[clips.length-1]?.end_sec ?? rawTime;
  };

  const transcriptTime = tlMode==="sequence" ? seqToRaw() : rawTime;

  // Klik på klip i sekvens: parkér rawTime, marker i transskription, START IKKE afspilning
  const handleSelectClip = (clip) => {
    setSelectedClipId(prev => prev === clip.clip_id ? null : clip.clip_id); // toggle
    setRawTime(clip.start_sec);  // parkér
    setTlMode("raw");
    setIsPlaying(false);
  };

  // Play-knap på klip: start afspilning fra klippets start
  const handlePlayFromClip = (clip) => {
    let off=0;
    for (const c of clips) { if(c.clip_id===clip.clip_id) break; off+=c.end_sec-c.start_sec; }
    setSeqTime(off); setTlMode("sequence"); setIsPlaying(true);
    setSelectedClipId(clip.clip_id);
  };

  // Resize af klip fra transskriptionen via håndtag
  const handleResizeClip = useCallback((clipId, edge, word) => {
    setClips(prev => prev.map(c => {
      if (c.clip_id !== clipId) return c;
      if (edge === "start") {
        // Flyt startpunkt — må ikke overstige end_sec
        const newStart = Math.min(word.start, c.end_sec - 0.5);
        return { ...c, start_sec: newStart };
      } else {
        // Flyt slutpunkt — må ikke gå under start_sec
        const newEnd = Math.max(word.end, c.start_sec + 0.5);
        return { ...c, end_sec: newEnd };
      }
    }));
  }, []);

  const handleAddClip = data => {
    const id = clipIdSeq; setClipIdSeq(v=>v+1);
    setClips(prev => { const arr=[...prev]; arr.splice(insertCursor,0,{...data,clip_id:id}); return arr; });
    setInsertCursor(c => c+1);
    setSelectedClipId(id);
  };

  const handleDeleteClip = id => {
    setClips(prev => { const idx=prev.findIndex(c=>c.clip_id===id); const arr=prev.filter(c=>c.clip_id!==id); if(insertCursor>idx) setInsertCursor(c=>Math.max(0,c-1)); return arr; });
    if (selectedClipId===id) setSelectedClipId(null);
  };

  const handleTranscriptInteract = () => { setTlMode("raw"); setIsPlaying(false); };

  /** Samme kick+polling som efter upload — bruges når man åbner et projekt der stadig transskriberes. */
  const kickTranscribeAndPollProject = async (pid) => {
    setProjectStatus('pending');
    fetch(`${API_BASE}/transcribe.php?project_id=${pid}`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
    }).catch((err) => console.warn('transcribe kick failed:', err));

    try {
      let attempts = 0;
      while (attempts < 300) {
        attempts++;
        const p = await API.loadProject(pid);
        setProjectStatus(p.status);

        if (p.status === 'error') {
          throw new Error(p.error_message || 'Transcription failed');
        }

        if (p.words && p.words.length > 0) {
          const newWords = p.words.map((w, i) => ({
            id: w.word_id,
            word: w.word,
            start: parseFloat(w.start_sec),
            end: parseFloat(w.end_sec),
            index: i
          }));
          setWords(newWords);
          setIsVideo(p.is_video || false);
          API.generateChapters(pid).then(res => {
            if (res.chapters && res.chapters.length > 0) {
              setChapters(res.chapters);
            }
          }).catch(err => {
            console.warn('Chapter generation failed, using auto-split:', err.message);
            setChapters(generateChapters(newWords));
          });
          setAudioUrl(`${API_BASE}/media.php?project_id=${pid}`);
          setProjectStatus('ready');
          API.loadProjects().then(data => setAllProjects(data.projects || []));
          return;
        }

        await new Promise(r => setTimeout(r, 2000));
      }
      throw new Error('Timeout: ventede for længe på transskription (over 10 min.)');
    } catch (error) {
      console.error('Transcription error:', error);
      setProjectStatus('error');
      setTranscriptionError(error.message || 'Ukendt fejl');
      API.loadProjects().then(data => setAllProjects(data.projects || []));
    }
  };

  // ── Upload & Transcription ────────────────────────────────────────────────
  const handleSelectProject = async (projectId, options = {}) => {
    const { timelineToLoad = null, openEditor = true } = options;
    try {
      console.log('🔄 Switching to project:', projectId);

      setClips([]);
      setTranscriptionError(null);

      const project = await API.loadProject(projectId);

      setProjectId(projectId);
      setHubLastOpenedProjectId(projectId);
      setProjectFilename(project.filename);
      setProjectStatus(project.status);
      setProjectAiUsage(project.ai_usage || { total_usd: 0, operations: 0 });
      setIsVideo(project.is_video || false);

      const wordsArr = project.words || [];
      if (wordsArr.length > 0) {
        const newWords = wordsArr.map((w, i) => ({
          id: w.word_id,
          word: w.word,
          start: parseFloat(w.start_sec),
          end: parseFloat(w.end_sec),
          index: i
        }));
        setWords(newWords);
        setAudioUrl(`${API_BASE}/media.php?project_id=${projectId}`);

        API.loadTimelines(projectId).then(data => {
          const timelines = data.timelines || [];
          setProjectTimelines(timelines);
          if (timelineToLoad) {
            const match = timelines.find((t) => t.timeline_id === timelineToLoad.timeline_id) || timelineToLoad;
            handleLoadTimeline(match);
          }
        });

        API.generateChapters(projectId).then(res => {
          if (res.chapters && res.chapters.length > 0) {
            setChapters(res.chapters);
          }
        }).catch(err => {
          console.warn('Chapter generation failed, using simple chapters');
          setChapters(generateChapters(newWords));
        });
      } else if (project.status === 'error') {
        setTranscriptionError(project.error_message || 'Transskription fejlede');
      } else if (project.status === 'pending' || project.status === 'processing') {
        await kickTranscribeAndPollProject(projectId);
      } else if (project.status === 'ready') {
        setTranscriptionError('Projektet har ingen ord (tom transskription).');
      } else {
        setWords([]);
      }

      setShowProjectSidebar(false);
      if (openEditor) {
        setViewMode('editor');
      }
    } catch (err) {
      console.error('Load projekt fejl:', err);
    }
  };

  const handleHubSelectProject = (selectedId) => {
    setHubSelectedProjectId(selectedId);
  };

  const handleOpenProjectFromHub = async (selectedId) => {
    await handleOpenProjectWithTimelineChoice(selectedId);
  };

  const handleOpenProjectWithTimelineChoice = async (selectedId) => {
    try {
      const project = (allProjects || []).find((p) => p.project_id === selectedId);
      const data = await API.loadTimelines(selectedId);
      const timelines = data.timelines || [];

      if (timelines.length === 0) {
        await handleSelectProject(selectedId, { openEditor: true });
        return;
      }

      setTimelineChoiceProjectId(selectedId);
      setTimelineChoiceProjectName(project?.filename || '');
      setTimelineChoiceTimelines(timelines);
      setShowTimelineChoiceModal(true);
    } catch (err) {
      console.error('Failed to load timeline choice:', err);
      await handleSelectProject(selectedId, { openEditor: true });
    }
  };

  const handleChooseNewTimeline = async () => {
    const pid = timelineChoiceProjectId;
    setShowTimelineChoiceModal(false);
    if (!pid) return;
    await handleSelectProject(pid, { openEditor: true });
  };

  const handleChooseSavedTimeline = async (timeline) => {
    const pid = timelineChoiceProjectId;
    setShowTimelineChoiceModal(false);
    if (!pid) return;
    await handleSelectProject(pid, { timelineToLoad: timeline, openEditor: true });
  };

  const requestTimelineName = (suggestedName) => new Promise((resolve) => {
    timelineNameResolver.current = resolve;
    setTimelineNameInput(suggestedName || '');
    setShowTimelineNameModal(true);
  });

  const resolveTimelineNameModal = (value) => {
    const resolver = timelineNameResolver.current;
    timelineNameResolver.current = null;
    setShowTimelineNameModal(false);
    if (resolver) resolver(value);
  };

  const handleSaveTimeline = async () => {
    if (!projectId || clips.length === 0) {
      console.warn('Ingen clips at gemme');
      return false;
    }
    
    // Auto-generate name med dato/tid
    const timestamp = new Date().toLocaleString('da-DK', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    const name = await requestTimelineName(`Timeline ${timestamp} (${clips.length} clips)`);
    if (!name) return false;
    
    try {
      await API.saveTimeline(projectId, name, clips);
      console.log('✅ Timeline gemt');
      
      // Reload projects list for at opdatere timeline count
      const projectsData = await API.loadProjects();
      setAllProjects(projectsData.projects || []);
      
      // Trigger sidebar timeline reload
      setTimelineSavedTrigger({ projectId, timestamp: Date.now() });
      
      console.log('🔄 Projects list reloaded after timeline save');
      return true;
    } catch (err) {
      console.error('Timeline fejl:', err);
      return false;
    }
  };

  const goToHubFromEditor = () => {
    setShowProjectSidebar(false);
    setViewMode('hub');
  };

  const handleGoHomeFromEditor = () => {
    if (clips.length === 0) {
      goToHubFromEditor();
      return;
    }
    setShowHomeConfirmModal(true);
  };

  const handleSaveAndLeaveHome = async () => {
    const ok = await handleSaveTimeline();
    if (!ok) return;
    setShowHomeConfirmModal(false);
    goToHubFromEditor();
  };

  const handleLeaveHomeWithoutSave = () => {
    setShowHomeConfirmModal(false);
    goToHubFromEditor();
  };

  const handleLoadTimeline = async (timeline) => {
    try {
      console.log('📂 Loading timeline:', timeline.name, 'with', timeline.clips?.length, 'clips');
      
      // Parse clips hvis de er JSON string
      const clips = typeof timeline.clips === 'string' 
        ? JSON.parse(timeline.clips) 
        : timeline.clips;
      
      if (!clips || clips.length === 0) {
        console.warn('Timeline har ingen clips');
        return;
      }
      
      // Reassign unique clip_id'er baseret på current clipIdSeq
      const clipsWithNewIds = clips.map((c, i) => {
        const newId = clipIdSeq + i;
        return { ...c, clip_id: newId };
      });
      
      // Opdater clipIdSeq counter
      setClipIdSeq(v => v + clips.length);
      
      // Set clips med nye ID'er
      setClips(clipsWithNewIds);
      console.log('✅ Timeline loaded:', clipsWithNewIds.length, 'clips with fresh IDs');
      
      // Luk sidebar efter load
      setShowProjectSidebar(false);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    }
  };

  const handleClearTimeline = () => {
    if (clips.length === 0) return;
    
    if (confirm(`Slet alle ${clips.length} clips fra timeline?`)) {
      // Clips cleares når nyt projekt loades
      setClips([]);
      console.log('🗑️ Timeline cleared');
    }
  };
  
  const handleDeleteTimeline = async (timelineId, projectId) => {
    if (!confirm('Slet denne timeline permanent?')) return;
    
    try {
      await API.deleteTimeline(timelineId);
      console.log('🗑️ Timeline deleted');
      
      // Reload timelines for projekt
      const data = await API.loadTimelines(projectId);
      setProjectTimelines(data.timelines || []);
      
      // Reload projects for at opdatere timeline count
      const projectsData = await API.loadProjects();
      setAllProjects(projectsData.projects || []);
    } catch (err) {
      console.error('Failed to delete timeline:', err);
    }
  };
  
  const handleDeleteProject = async (projectIdToDelete) => {
    if (!confirm('ADVARSEL: Slet hele projektet permanent?\n\nDette sletter:\n- Videofilen\n- Transskription\n- Alle timelines\n\nDenne handling kan ikke fortrydes!')) return;
    
    try {
      const deletedCurrent = Number(projectIdToDelete) === Number(projectId);
      await API.deleteProject(projectIdToDelete);
      console.log('🗑️ Project deleted');
      
      // Reload projects list
      const data = await API.loadProjects();
      setAllProjects(data.projects || []);
      if (hubSelectedProjectId === projectIdToDelete) {
        setHubSelectedProjectId(null);
      }
      
      if (deletedCurrent) {
        setProjectId(null);
        setWords([]);
        setClips([]);
        setProjectAiUsage({ total_usd: 0, operations: 0 });
        setViewMode('hub');
      }
    } catch (err) {
      console.error('Failed to delete project:', err);
      alert('Kunne ikke slette projekt: ' + err.message);
    }
  };

  const handleUploadComplete = async (response) => {
    console.log('Upload complete response:', response);
    setTranscriptionError(null);

    // Håndter pending state (upload 100% men server ikke færdig endnu)
    if (response.pending) {
      setProjectId(-1); // Temporary ID så interface vises
      setProjectFilename(response.filename || 'Uploading...');
      setProjectStatus('processing');
      setViewMode('editor');
      return;
    }
    
    const pid = parseInt(response.project_id);
    if (!pid) {
      console.error('Upload fejl:', response);
      return;
    }
    
    setProjectId(pid);
    setProjectFilename(response.filename || 'Video');
    setViewMode('editor');
    API.loadProjects().then(data => setAllProjects(data.projects || []));
    // Hvis status allerede er 'ready' fra blocking upload:
    if (response.status === 'ready' && response.word_count) {
      setProjectStatus('ready');
      // Load project direkte
      const project = await API.loadProject(pid);
      if (project.words && project.words.length > 0) {
        const newWords = project.words.map((w, i) => ({
          id: w.word_id,
          word: w.word,
          start: parseFloat(w.start_sec),
          end: parseFloat(w.end_sec),
          index: i
        }));
        setWords(newWords);
        setIsVideo(project.is_video || false);
        setAudioUrl(`${API_BASE}/media.php?project_id=${pid}`);
        // Generer kapitler
        API.generateChapters(pid).then(res => {
          if (res.chapters && res.chapters.length > 0) {
            setChapters(res.chapters);
          }
        }).catch(err => {
          console.warn('Chapter generation failed:', err.message);
          setChapters(generateChapters(newWords));
        });
        console.log('Done! Words loaded:', newWords.length);

        
        // Reload projects list
        API.loadProjects().then(data => {
          setAllProjects(data.projects || []);
        });        return; // Færdig, ingen polling nødvendig
      }
    }
    
    await kickTranscribeAndPollProject(pid);
  };


  const handleExport = async (format) => {
    if (!projectId) return;
    try {
      const blob = await API.exportTimeline(projectId, clips, format, projectFilename);
      const ext = { fcpxml:'fcpxml', edl:'edl', srt:'srt', premiere_xml:'xml' }[format] || 'xml';
      API.downloadFile(blob, `${projectFilename}.${ext}`);
    } catch (error) {
      console.error('Export fejl:', error);
    }
  };

  return (
    <div className="app-root">
      <style>{CSS + UPLOAD_CSS}</style>
      <TopBar
        onUploadClick={() => setShowUploadModal(true)}
        onProjectsClick={() => { if (viewMode === 'editor') setShowProjectSidebar(true); }}
        status={viewMode === 'editor' ? projectStatus : null}
        filename={viewMode === 'hub' ? 'Video2text-editor' : projectFilename}
        showUpload={false}
        showHome={viewMode === 'editor'}
        onHomeClick={handleGoHomeFromEditor}
        aiUsageText={viewMode === 'editor' && Number(projectAiUsage?.total_usd || 0) > 0
          ? `AI ~ $${Number(projectAiUsage.total_usd).toFixed(Number(projectAiUsage.total_usd) < 0.01 ? 3 : 2)}`
          : ''}
      />
      
      <ProjectSidebar
        isOpen={viewMode === 'editor' && showProjectSidebar}
        onClose={() => setShowProjectSidebar(false)}
        projects={allProjects}
        currentProjectId={projectId}
        onSelectProject={handleOpenProjectWithTimelineChoice}
        onNewProject={() => { setShowProjectSidebar(false); setShowUploadModal(true); }}
        onLoadTimeline={handleLoadTimeline}
        onDeleteTimeline={handleDeleteTimeline}
        onDeleteProject={handleDeleteProject}
        onTimelineSaved={timelineSavedTrigger}
      />

      {viewMode === 'hub' ? (
        <ProjectHub
          projects={allProjects}
          selectedProjectId={hubSelectedProjectId}
          lastOpenedProjectId={hubLastOpenedProjectId}
          onSelectProject={handleHubSelectProject}
          onOpenProject={handleOpenProjectFromHub}
          onDeleteProject={handleDeleteProject}
          onUploadClick={() => setShowUploadModal(true)}
        />
      ) : (
        <>
          {projectId && words.length === 0 && !transcriptionError && (
            <div className="transcribing-overlay">
              <div className="transcribing-box">
                <div className="transcribing-spinner"></div>
                <div className="transcribing-text">Transkriberer...</div>
                <div className="transcribing-subtext">{projectFilename}</div>
                <button
                  type="button"
                  className="welcome-upload-btn"
                  style={{ marginTop: 24, opacity: 0.9 }}
                  onClick={() => {
                    setProjectId(null);
                    setWords([]);
                    setTranscriptionError(null);
                    setProjectStatus(null);
                    setViewMode('hub');
                  }}
                >
                  Stop og gå tilbage
                </button>
              </div>
            </div>
          )}
          {projectId && words.length === 0 && transcriptionError && (
            <div className="transcribing-overlay">
              <div className="transcribing-box" style={{ maxWidth: 480 }}>
                <div className="transcribing-text" style={{ color: '#FCA5A5' }}>Transskription fejlede</div>
                <div className="transcribing-subtext" style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>{transcriptionError}</div>
                <button type="button" className="welcome-upload-btn" style={{ marginTop: 20 }} onClick={() => { setProjectId(null); setWords([]); setTranscriptionError(null); setProjectStatus(null); setViewMode('hub'); }}>
                  Prøv igen
                </button>
              </div>
            </div>
          )}

          <div className="app-body">
            <TranscriptView
              words={words} clips={clips}
              currentTime={transcriptTime}
              selectedClipId={selectedClipId}
              onAddClip={handleAddClip}
              onSeek={t=>{ setRawTime(t); setTlMode("raw"); }}
              onTranscriptInteract={handleTranscriptInteract}
              onResizeClip={handleResizeClip}
              chapters={chapters}
            />
            <VideoPreview
              currentTime={tlMode==="sequence"?seqToRaw():rawTime}
              duration={rawDur} isPlaying={isPlaying}
              onPlayPause={()=>setIsPlaying(v=>!v)}
              onSeek={t=>{ setRawTime(t); setTlMode("raw"); setIsPlaying(false); }}
              audioUrl={audioUrl}
              onTimeUpdate={t=>setRawTime(t)}
              onDurationChange={d=>setRawDur(d)}
             isVideo={isVideo} />
            <SequencePanel
              clips={clips}
              currentTime={tlMode==="sequence"?seqToRaw():rawTime}
              insertCursor={insertCursor}
              selectedClipId={selectedClipId}
              onInsertCursorChange={setInsertCursor}
              onSelectClip={handleSelectClip}
              onPlayFromClip={handlePlayFromClip}
              onDeleteClip={handleDeleteClip}
              onReorderClips={setClips}
              onSave={handleSaveTimeline}
              onSaveTimeline={handleSaveTimeline} onClearTimeline={handleClearTimeline} onExport={handleExport}
            />
          </div>
          <BottomTimeline
            mode={tlMode} rawDuration={rawDur} rawCurrentTime={rawTime}
            clips={clips} seqTime={seqTime}
            onRawSeek={t=>{ setRawTime(t); setIsPlaying(false); }}
            onSeqSeek={t=>{ setSeqTime(t); setIsPlaying(false); }}
            insertCursor={insertCursor}
            selectedClipId={selectedClipId}
          />
        </>
      )}
      
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
      />
      <TimelineChoiceModal
        isOpen={showTimelineChoiceModal}
        projectName={timelineChoiceProjectName}
        timelines={timelineChoiceTimelines}
        onNewTimeline={handleChooseNewTimeline}
        onChooseTimeline={handleChooseSavedTimeline}
        onCancel={() => setShowTimelineChoiceModal(false)}
      />
      <HomeConfirmModal
        isOpen={showHomeConfirmModal}
        onSaveAndLeave={handleSaveAndLeaveHome}
        onLeaveWithoutSave={handleLeaveHomeWithoutSave}
        onCancel={() => setShowHomeConfirmModal(false)}
      />
      <TimelineNameModal
        isOpen={showTimelineNameModal}
        value={timelineNameInput}
        onChange={setTimelineNameInput}
        onConfirm={() => resolveTimelineNameModal(timelineNameInput.trim())}
        onCancel={() => resolveTimelineNameModal('')}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CSS
// ═══════════════════════════════════════════════════════════════════════════════
